<template>
  <div
    v-if="histModel.length >= 1 && !loading"
    class="audit-toolbar"
  >
    <div class="audit-filter">
      <label>{{ t('common.author') }}</label>
      <MultiSelectDropdown
        :options="authorsList"
        v-model="selectedAuthors"
        buttonWidth="100%"
      />
    </div>
    <div class="audit-filter">
      <label>{{ t('filter.dateFrom') }}</label>
      <DatePicker v-model="startDate" />
    </div>
    <div class="audit-filter">
      <label>{{ t('filter.dateTo') }}</label>
      <DatePicker v-model="endDate" />
    </div>
    <div class="audit-filter audit-filter-btn">
      <button
        type="button"
        class="btn page-header-btn page-header-btn--secondary"
        @click="resetFilters"
      >
        {{ t('filter.reset') }}
      </button>
      <button
        v-if="props.onExport"
        type="button"
        class="btn page-header-btn page-header-btn--primary ms-2"
        :disabled="props.exportPending"
        @click="props.onExport"
      >
        <span
          v-if="props.exportPending"
          class="spinner-border spinner-border-sm me-1"
        ></span>
        {{ t('common.export') }}
      </button>
    </div>
  </div>
  <div v-if="loading" class="text-center py-4">
    <i class="bi bi-arrow-clockwise spinning"></i>
    <div class="mt-2">{{ t('common.loadingHistory') }}</div>
  </div>
  <div v-else-if="isEmpty" class="text-center py-4 text-muted">
    {{ t('empty') }}
  </div>
  <div v-else class="history-main history-table-card">
    <!-- Шапка вне прокрутки: тянется на всю ширину, скролл только у строк. -->
    <div class="history-table-head">
      <table class="table apple-table audit-trail-table history mb-0">
        <colgroup>
          <col class="history-col-author" />
          <col class="history-col-date" />
          <col class="history-col-object" />
          <col :class="{ 'history-col-action': showDetailsColumn }" />
          <col v-if="showDetailsColumn" />
        </colgroup>
        <thead>
          <tr>
            <th class="history-col-author">{{ t('common.author') }}</th>
            <th class="history-col-date">{{ t('col.date') }}</th>
            <th class="history-col-object">{{ t('col.object') }}</th>
            <th>{{ t('col.action') }}</th>
            <th v-if="showDetailsColumn">{{ t('col.details') }}</th>
          </tr>
        </thead>
      </table>
    </div>
    <div class="table-scroll">
      <table
        class="table apple-table audit-trail-table history"
        @click="onTableClick"
      >
        <colgroup>
          <col class="history-col-author" />
          <col class="history-col-date" />
          <col class="history-col-object" />
          <col :class="{ 'history-col-action': showDetailsColumn }" />
          <col v-if="showDetailsColumn" />
        </colgroup>
        <tbody>
          <template
            v-for="(step, stepIdx) in isAuditTrail ? paginatedModel : filteredModel"
            :key="step.id ?? `${step.entity || 'none'}:${stepIdx}`"
          >
            <tr v-if="step.changes.length">
              <td
                :rowspan="step.changes.length"
                class="history-col-author"
              >
                <div class="person-name">
                  <div
                    class="person-avatar"
                    :style="avatarStyle(step.author)"
                  >
                    {{ authorInitials(step.author) }}
                  </div>
                  <span class="fw-semibold">{{ step.author }}</span>
                </div>
              </td>
              <td
                :rowspan="step.changes.length"
                class="date-cell history-col-date"
              >
                <span class="date-cell__date">{{ historyDatePart(step.date) }}</span>
                <span
                  v-if="historyTimePart(step.date)"
                  class="date-cell__time"
                >{{ historyTimePart(step.date) }}</span>
              </td>
              <td
                :rowspan="step.changes.length"
                class="history-object-cell history-col-object"
              >
                <span
                  v-if="step.entity"
                  v-html="entityText(step)"
                ></span>
                <template v-if="step.entityUpload">
                  <span>&nbsp;</span>
                  <a
                    href="#"
                    class="link-primary text-decoration-underline"
                    :data-doc-sec="step.entityUpload.secure_filename"
                    :data-doc-fn="step.entityUpload.filename"
                  >{{ step.entityUpload.filename }}</a>
                </template>
              </td>
              <td class="history-action-cell">
                <span
                  v-if="step.changes[0].action"
                  v-html="step.changes[0].action"
                ></span>
              </td>
              <td v-if="showDetailsColumn" class="history-details-cell">
                <span
                  v-if="step.changes[0].text"
                  v-html="step.changes[0].text"
                ></span>
              </td>
            </tr>

            <tr
              v-for="(chg, idx) in step.changes.slice(1)"
              :key="`${step.id ?? stepIdx}-${idx + 1}`"
            >
              <td class="history-action-cell">
                <span
                  v-if="chg.action"
                  v-html="chg.action"
                ></span>
              </td>
              <td v-if="showDetailsColumn" class="history-details-cell">
                <span
                  v-if="chg.text"
                  v-html="chg.text"
                ></span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <Pagination
      v-if="isAuditTrail"
      :total-item="serverMode ? (serverTotalResolved ?? 0) : filteredModel.length"
      :current-page="currentPage"
      :on-page="onPage"
      :on-page-arr="[10, 20, 50]"
      @page-change="handlePageChange"
    />
  </div>

  <Teleport to="body">
    <Docviewer
      v-if="docViewerVisible && docUpload"
      :upload="docUpload"
      :versioning="false"
      :shift-left="true"
      @hidden="onDocviewerHidden"
    />
  </Teleport>
</template>

<script setup>
import { computed, isReactive, isRef, toValue, ref, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import DOMPurify from 'dompurify'
import { get as _get } from 'lodash-es'
import {
  models,
  fields,
  values,
  repeatingHistoryEntity,
  formatRelationHistoryValue,
  entityDocumentDivisionLabel,
  linkifyEntityUploadFromChange,
  isEntityDocumentHistoryEntry,
  isEntityDocumentHistoryAction,
  isEntityDocumentAddedAction,
  isEntityDocumentDeletedAction,
  escapeHtmlText,
  userHistoryFieldLabel,
  shouldSkipUserFacsimileHistoryChange,
  isFacsimileBinaryHistoryText,
  isFacsimileBinaryHistoryValue,
  formatNotificationSettingHistoryLabel,
} from '@/lib/history'
import { productRiskCategoryLabels } from '@/lib/historyProductsFields'
import { commons } from '@/lib/transformer'
import { historyAction, historyFieldLabel, historyPhrase, historyPo, historyKnownLabel, historySuborderAction } from '@/lib/historyPhrases'
import { gmpProductTitle, uiPrefersEnglish } from '@/lib/localizedTitle'
import { getRoleCaption } from '@/lib/roleLabels'
import { useConfigsStore } from '@/stores/configs'
import MultiSelectDropdown from '@/components/dropdowns/MultiSelectDropdown.vue'
import DatePicker from '@/components/datepicker.vue'
import Pagination from '@/components/pagination/pagination.vue'
import { Modal as Docviewer } from 'soykajs/components/docviewer'
import { useModalStack } from '@/stores/modalstack'
import { parse } from 'date-fns'

const { t, locale } = useI18n()
const router = useRouter()
const modalStack = useModalStack()
useConfigsStore().fillEntity('submissions')
const emit = defineEmits(['update:recordsCount'])

const AVATAR_PALETTES = [
  { background: 'linear-gradient(135deg, var(--accent-cyan), var(--accent-cyan-light))', color: 'var(--berry-dark)' },
  { background: 'linear-gradient(135deg, #ddd6fe, #ede9fe)', color: '#5b21b6' },
  { background: 'linear-gradient(135deg, #fecdd3, #ffe4e6)', color: '#9f1239' },
  { background: 'linear-gradient(135deg, #bbf7d0, #dcfce7)', color: '#166534' },
  { background: 'linear-gradient(135deg, #fde68a, #fef3c7)', color: '#92400e' },
]

/** Поля карточки на стартовой для подписей истории (до первого вызова formatDiff). */
const HOME_CARD_ROOTS = ['product_journey', 'clinical_trials', 'market_access']
const HOME_JOURNEY_STAGES = ['clinical', 'market_access', 'release']
const HOME_JOURNEY_DATES = ['submission_plan', 'submission_actual', 'approval_plan', 'approval_actual']
const HOME_TRIAL_FIELDS = ['code', 'start_plan', 'start_actual', 'end_plan', 'end_actual']
const HOME_MARKET_BLOCKS = ['vednp', 'clinRecs']
const HOME_MARKET_FIELDS = ['value', 'plan_date', 'actual_date']

function authorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

/** «22.07.2026, 23:25» → дата и время на разных строках */
function historyDatePart(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const comma = raw.indexOf(',')
  return comma === -1 ? raw : raw.slice(0, comma).trim()
}

function historyTimePart(value) {
  const raw = String(value || '').trim()
  const comma = raw.indexOf(',')
  return comma === -1 ? '' : raw.slice(comma + 1).trim()
}

function avatarStyle(name) {
  const str = String(name || '')
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  }
  return AVATAR_PALETTES[hash % AVATAR_PALETTES.length]
}

function historyIterablePathRoot(change) {
  const parts = historyFieldPath(change)
  if (parts.length) {
    return String(parts[0])
  }
  if (Array.isArray(change.path)) {
    return String(change.path[0] ?? '')
  }
  return String(change.path ?? '')
}

function isHistoryValueEmpty(value) {
  if (value == null) {
    return true
  }
  const s = String(value).trim()
  return s === '' || s === 'undefined' || s === 'null' || s === 'None'
}

function plainHistoryText(value) {
  return String(value ?? '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
}

/** Обе стороны истории — одна и та же дата (день или квартал), поле не меняли. */
function isSameDisplayedHistoryDate(oldValue, newValue) {
  const oldText = plainHistoryText(oldValue)
  const newText = plainHistoryText(newValue)
  if (!oldText || oldText !== newText) {
    return false
  }
  return /^\d{2}\.\d{2}\.\d{4}$/.test(oldText) || /^[qQ][1-4]\s+\d{4}$/.test(oldText)
}

function historyVisibleText(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
}

function isHistoryChangeTextEmpty(text) {
  if (!text) {
    return true
  }
  const plain = String(text)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return !plain
}

/** Как значение лежит в History, до подстановки подписи справочника. */
function historyStoredText(value) {
  if (value == null) {
    return ''
  }
  const text = String(value).trim()
  if (text === 'None' || text === 'null' || text === 'undefined') {
    return ''
  }
  return text
}

/**
 * Возвращает имя пользователя, чей доступ изменили, отдельно от переводимой подписи поля.
 *
 * @param {string} modelName - Модель записи истории.
 * @param {object} change - Изменение доступа.
 * @returns {string} имя пользователя или пустая строка для остальных изменений.
 */
function restrictedAccessSubjectName(modelName, change) {
  if (modelName !== 'Products' || historyFieldPath(change)[0] !== 'restricted_access') {
    return ''
  }
  return historyStoredText(change.user_label) || historyStoredText(change.user_id)
}

/**
 * Текст комментария для колонки «Детали»: без глагола из «Действия».
 * Старые записи хранили «добавлен комментарий "…"» / «изменен комментарий с "…" на "…"».
 *
 * @param {string} raw comment из History
 * @param {'added'|'edited'|'deleted'} kind
 * @returns {string}
 */
function formatCommentHistoryDetails(raw, kind) {
  let text = String(raw || '').trim()
  if (!text) {
    return ''
  }
  if (kind === 'edited') {
    const legacy = text.match(
      /^изменен комментарий\s+с\s+"([^"]*)"\s+на\s+"([^"]*)"\s*$/i
    )
    const arrow = text.match(/^"([^"]*)"\s*→\s*"([^"]*)"\s*$/)
    const pair = legacy || arrow
    if (pair) {
      return `${historyPhrase('connectors.from')} "${pair[1]}" ${historyPhrase('connectors.to')} "${pair[2]}"`
    }
    text = text.replace(/^изменен комментарий\s*/i, '').trim()
  } else if (kind === 'added') {
    text = text.replace(/^добавлен комментарий\s*/i, '').trim()
  } else if (kind === 'deleted') {
    text = text.replace(/^удал[её]н комментарий\s*/i, '').trim()
  }
  if (
    (text.startsWith('"') && text.endsWith('"'))
    || (text.startsWith("'") && text.endsWith("'"))
  ) {
    text = text.slice(1, -1)
  }
  return text
}

/** Подпись поля, если formatDiff не дошёл до обычной сборки строки. */
function resolveDisplayedFieldLabel(modelName, change) {
  const collectionLabel =
    resolveRegistrationCertificatesFieldLabel(change)
    || resolveProductCollectionFieldLabel(change)
    || resolveHomeCardFieldLabel(change)
  if (collectionLabel) {
    return collectionLabel
  }
  const fieldPath = historyFieldPath(change)
  const fullKey = fieldPath.length ? fieldPath.join('.') : ''
  let label = fieldPath.length
    ? _get(fields, [modelName, ...fieldPath]) || _get(fields, fieldPath)
    : null
  if (label && typeof label === 'object' && !(label instanceof Function)) {
    label = label.label || label.title || null
  }
  const leaf = fieldPath.length ? String(fieldPath[fieldPath.length - 1]) : ''
  if (typeof label !== 'string' && leaf) {
    const leafLabel = _get(fields, [modelName, leaf]) || _get(fields, [leaf])
    if (typeof leafLabel === 'string') {
      label = leafLabel
    }
  }
  const translated = historyFieldLabel(modelName, fullKey || leaf, typeof label === 'string' ? label : null)
  if (typeof translated === 'string' && translated && translated !== fullKey) {
    return translated
  }
  if (typeof label === 'string' && label) {
    return label
  }
  return fullKey
}

/** Запасные строки истории: подписи полей, даты и текст справочника, не сырой ключ. */
function fallbackFieldChanges(row, related) {
  const changes = Array.isArray(row?.changes) ? row.changes : []
  const rows = []
  for (const change of changes) {
    if (!change) {
      continue
    }
    if (change.action === 'delete_object' || change.action === 'add_object') {
      const actionKey = change.action === 'delete_object' ? 'deleted' : 'added'
      const child = deletedSubjectLabel(row, related)
      rows.push({
        action: historyAction(actionKey),
        text: child,
      })
      continue
    }
    if (change.action !== 'values_changed' && change.action !== 'type_changes') {
      continue
    }
    const rawNext = historyStoredText(change.value)
    const rawPrev = historyStoredText(change.old_value)
    if (rawNext === rawPrev) {
      continue
    }
    if (!change.relation) {
      change.relation = findClass(
        related?.map || [],
        row.model_name,
        historyRelationField(change)
      )
    }
    let nextVal = rawNext
    let prevVal = rawPrev
    try {
      nextVal = historyStoredText(getVal(row, change, related)) || rawNext
      prevVal = historyStoredText(getVal(row, change, related, 'old_value')) || rawPrev
    } catch (err) {
      console.warn('[history] fallback value format failed', row?.model_name, change.path, err)
    }
    if (nextVal === prevVal && (nextVal !== '' || rawNext === rawPrev)) {
      continue
    }
    const label = escapeHtmlText(resolveDisplayedFieldLabel(row.model_name, change))
    const parts = []
    const subjectName = restrictedAccessSubjectName(row.model_name, change)
    if (subjectName) parts.push(escapeHtmlText(subjectName))
    parts.push(`"${label}"`)
    if (prevVal) {
      parts.push(`${historyPhrase('connectors.from')} ${escapeHtmlText(prevVal)}`)
    }
    parts.push(`${historyPhrase('connectors.to')} ${escapeHtmlText(nextVal) || '\u00a0'}`)
    rows.push({
      action: historyAction('changed'),
      text: parts.join(' '),
    })
  }
  return rows
}

/** Имя дочерней сущности в «удалено», чтобы это не выглядело как удаление карточки. */
function deletedSubjectLabel(row, related) {
  const repeating = repeatingHistoryEntity(row, related)
  if (repeating) {
    return repeating
  }
  if (!row?.model_name || !props.modelName || row.model_name === props.modelName) {
    return ''
  }
  try {
    const rel = resolveHistoryEntityRelated(related, row.model_name, row.model_id)
    const named = models[row.model_name]?.entityName?.(row, rel, false)
    if (named) {
      return String(named)
    }
  } catch {
    /* подпись необязательна */
  }
  return historyPhrase('entity.generic', {
    model: row.model_name,
    id: row.model_id ?? '',
  })
}

/** Создание или удаление вложенной сущности, у которой есть своя подпись. */
function isChildLifecycleRecord(row) {
  if (!row?.model_name || !props.modelName || row.model_name === props.modelName) {
    return false
  }
  if (typeof models[row.model_name]?.entityName !== 'function') {
    return false
  }
  return (row.changes || []).some(
    change => change?.action === 'add_object' || change?.action === 'delete_object'
  )
}

/** Колонка «Объект», даже если разбор changes упал. */
function safeHistoryEntity(related, row) {
  try {
    const repeating = repeatingHistoryEntity(row, related)
    if (repeating) {
      return repeating
    }
    if (isChildLifecycleRecord(row)) {
      const own = models[row.model_name].entityName(
        row,
        resolveHistoryEntityRelated(related, row.model_name, row.model_id),
        false
      )
      if (own) {
        return own
      }
    }
    const rel = resolveHistoryEntityRelated(related, props.modelName || row?.model_name, props.modelId || row?.model_id)
    const cardModel = models[props.modelName]
    if (props.modelName && props.modelName !== 'audit_trail' && props.modelName !== 'Dropdowns' && cardModel?.entityName) {
      const cardChange = {
        ...row,
        model_name: props.modelName,
        model_id: props.modelId,
      }
      const cardEntity = cardModel.entityName(cardChange, rel, false)
      if (cardEntity) {
        return cardEntity
      }
    }
    const own = models[row?.model_name]?.entityName?.(
      row,
      resolveHistoryEntityRelated(related, row?.model_name, row?.model_id),
      false
    )
    if (own) {
      return own
    }
  } catch (err) {
    console.warn('[history] entity label failed', row?.model_name, err)
  }
  return historyPhrase('entity.generic', {
    model: row?.model_name || '?',
    id: row?.model_id ?? '?',
  })
}

const USER_ITERABLE_HISTORY_FIELDS = ['roles', 'notification_settings']

function normalizeHistoryPath(path) {
  if (path == null || path === '') {
    return []
  }
  if (Array.isArray(path)) {
    return path
  }
  if (typeof path === 'string' && path.includes('.')) {
    return path.split('.').map(seg => (/^\d+$/.test(seg) ? Number(seg) : seg))
  }
  return [String(path)]
}

function formatRegistrationCertificatesHistoryLabel(path, change) {
  const full = normalizeHistoryPath(path)
  const leaf = full.length >= 3 ? full[full.length - 1] : null
  let label = t('registrationCertificates')
  if (leaf === 'date') label = t('common.registrationDate')
  else if (leaf === 'certificate') label = t('registrationCertificateNumber')
  else {
    // Добавление/удаление всей пары: № РУ уже в значении — не дублировать
    return label
  }

  const ru = (change?.ru_certificate || '').toString().trim()
  if (ru) return t('registrationCertificateReference', { label, number: ru })

  const idxSeg = full.find(seg => /^\d+$/.test(String(seg)))
  const idx = Number(idxSeg)
  if (Number.isFinite(idx) && idx >= 0 && idxSeg !== undefined) {
    return t('registrationCertificatePosition', { label, position: idx + 1 })
  }
  return label
}

function resolveRegistrationCertificatesFieldLabel(change) {
  const full = normalizeHistoryPath(change?.path)
  if (full[0] !== 'registration_certificates') return null
  return formatRegistrationCertificatesHistoryLabel(full, change)
}

function historyFieldPath(change) {
  return normalizeHistoryPath(change.path).filter(segment => !/^\d+$/.test(String(segment)))
}

/** Лист пути: mah_en, а не индекс блока и не pack_blocks. */
function historyRelationField(change) {
  const parts = historyFieldPath(change)
  return parts.length ? String(parts[parts.length - 1]) : ''
}

function resolveTaxStatusHistoryLabel(change) {
  const fallback = change.status_label || change.status_key || ''
  const key = change.status_key
  if (!key) {
    return fallback
  }
  const configStore = useConfigsStore()
  const options = toValue(configStore.get('taxes.status', [])) || []
  const found = options.find(item => item?.key === key)
  return found?.value || fallback
}

const docViewerVisible = ref(false)
const docUpload = ref(null)

function setHistoryDocviewerBackdrop(active) {
  document.body.classList.toggle('history-docviewer-active', active)
}

function onDocviewerHidden() {
  docViewerVisible.value = false
  setHistoryDocviewerBackdrop(false)
}

watch(docViewerVisible, visible => {
  if (!visible) {
    setHistoryDocviewerBackdrop(false)
  }
})

onUnmounted(() => {
  setHistoryDocviewerBackdrop(false)
})

function openDocviewerBySecureFilename(secureFilename, displayFilename) {
  if (!secureFilename) return
  const fn = (displayFilename || secureFilename).trim()
  docUpload.value = { secure_filename: secureFilename, filename: fn }
  setHistoryDocviewerBackdrop(true)
  docViewerVisible.value = true
}

function onTableClick(e) {
  const docLink = e?.target?.closest?.('a[data-doc-sec]')
  if (docLink) {
    e.preventDefault?.()
    e.stopPropagation?.()
    openDocviewerBySecureFilename(
      docLink.getAttribute('data-doc-sec'),
      docLink.getAttribute('data-doc-fn')
    )
    return
  }
  const entityLink = e?.target?.closest?.('a[href]')
  if (!entityLink) return
  const href = entityLink.getAttribute('href') || ''
  // Карточка реестра: закрываем историю и открываем маршрут без перезагрузки.
  if (!href.startsWith('/') || href.startsWith('//')) return
  e.preventDefault?.()
  e.stopPropagation?.()
  modalStack.closeAll()
  router.push(href).catch(() => {})
}

const taxesHistorySkipFields = new Set([
  'id',
  'date_created',
  'deleted',
  'auto_created',
  'product_label',
  'activity_label',
  'payment_order',
  'submitted_at',
  'service_note',
  'epr',
  'epr_archive',
  'other_documents',
  'uploads',
])

const productsHistorySkipFields = new Set([
  'normative_document',
  'normative_document_archive',
  'uploads',
])

// В аудит-трейле только сущности со ссылкой на карточку реестра.
const auditTrailExcludedModels = new Set([
  'ProductDosageForms',
  'ProductDosageFormItems',
  'ProductIndications',
  'ProductManufacturers',
  'ProductAfsApis',
  'ProductAfsApiManufacturers',
  'GmpForSubmissionAction',
  'Uploads',
])

const sitesHistorySkipFields = new Set([
  'manufacturer_country_certificate',
  'manufacturer_country_certificate_archive',
  'license',
  'license_archive',
  'certificate',
  'certificate_archive',
  'uploads',
])

const medicinesHistorySkipFields = new Set(['packing', 'manufacturer'])

// Выше histModel: счётчик записей считается сразу и уже читает эти ключи.
const PRODUCT_COLLECTION_LEAVES = {
  pack_blocks: {
    pack_size_en: 'Pack size ENG',
    pack_size_ru: 'Pack size RUS',
    shelf_life_ru: 'Shelf-life RUS',
    storage_conditions_ru: 'Storage conditions RUS',
  },
  po_entries: {
    po_number: 'PO number',
    year: 'PO date',
  },
}

const props = defineProps({
  list: Object,
  loading: { type: Boolean, default: true },
  limit: { type: Number, default: null },
  modelName: String,
  modelId: Number,
  subject: { type: String, default: '' },
  onSelect: { type: Function, default: null },
  onExport: { type: Function, default: null },
  exportPending: { type: Boolean, default: false },
  onQueryChange: { type: Function, default: null },
  serverTotal: { type: [Number, Object], default: null },
  serverAuthors: { type: [Array, Object], default: null },
})
const histModel = computed(() => {
  // Раскладка участвует в подписях; без чтения locale смена языка не пересобирает строки.
  void locale.value
  return _pick(props.list ?? {})
})
const isEmpty = computed(() => histModel.value && histModel.value.length === 0)
const currentPage = ref(1)
const onPage = ref(20)

function getExportParams() {
  return {
    authors: toValue(selectedAuthors) || [],
    dateFrom: toValue(startDate) || null,
    dateTo: toValue(endDate) || null,
  }
}

defineExpose({
  resetFilters,
  getExportParams,
})

const isAuditTrail = computed(() => props.modelName === 'audit_trail')
/** Audit Trail Досье — серверные фильтры; для него же колонка «Детали». */
const serverMode = computed(
  () => isAuditTrail.value && typeof props.onQueryChange === 'function'
)
/** История объекта всегда; в Audit Trail — только для Досье (rename/ACL/версии…). */
const showDetailsColumn = computed(() => !isAuditTrail.value || serverMode.value)
const serverTotalResolved = computed(() => {
  const v = props.serverTotal
  return v == null ? null : Number(toValue(v))
})

const handlePageChange = ({ page, onPage: newOnPage }) => {
  currentPage.value = page
  onPage.value = Number(newOnPage)
  if (serverMode.value) {
    props.onQueryChange({ page: currentPage.value, onPage: onPage.value })
  }
}

const selectedAuthors = ref([])
const startDate = ref(null)
const endDate = ref(null)

const authorsList = computed(() => {
  if (serverMode.value && props.serverAuthors) {
    const list = toValue(props.serverAuthors) || []
    return list.map(a => ({ value: a, label: a }))
  }
  const uniq = Array.from(new Set(histModel.value.map(s => s.author)))
  return uniq.sort().map(a => ({ value: a, label: a }))
})

const skipFilterQuery = ref(false)

function resetFilters({ emitQuery = true } = {}) {
  skipFilterQuery.value = !emitQuery
  selectedAuthors.value = []
  startDate.value = null
  endDate.value = null
  currentPage.value = 1
  skipFilterQuery.value = false
}

watch(
  [selectedAuthors, startDate, endDate],
  () => {
    if (skipFilterQuery.value) return
    currentPage.value = 1
    if (serverMode.value) {
      props.onQueryChange({
        page: 1,
        authors: [...selectedAuthors.value],
        dateFrom: startDate.value,
        dateTo: endDate.value,
      })
    }
  },
  { flush: 'sync' }
)

function parseStepDate(str) {
  let d = parse(str, 'dd.MM.yyyy, HH:mm:ss', new Date())
  if (Number.isNaN(d.getTime())) d = parse(str, 'dd.MM.yyyy, HH:mm', new Date())
  return d
}

const filteredModel = computed(() => {
  if (serverMode.value) {
    return histModel.value
  }
  return histModel.value.filter(step => {
    if (selectedAuthors.value.length > 0 && !selectedAuthors.value.includes(step.author)) {
      return false
    }
    const d = parseStepDate(step.date)
    if (startDate.value) {
      const from = new Date(startDate.value)
      from.setHours(0, 0, 0)
      if (d < from) return false
    }
    if (endDate.value) {
      const to = new Date(endDate.value)
      to.setHours(23, 59, 59)
      if (d > to) return false
    }
    return true
  })
})

watch(
  () =>
    serverMode.value
      ? Number(serverTotalResolved.value ?? 0)
      : filteredModel.value.length,
  total => {
    if (serverMode.value && serverTotalResolved.value == null) return
    const maxPage = Math.max(1, Math.ceil(total / onPage.value) || 1)
    if (currentPage.value > maxPage) {
      currentPage.value = maxPage
    }
  }
)

const paginatedModel = computed(() => {
  if (serverMode.value) {
    return filteredModel.value
  }
  const start = (currentPage.value - 1) * onPage.value
  const end = start + onPage.value
  return filteredModel.value.slice(start, end)
})

const displayedModel = computed(() =>
  isAuditTrail.value ? paginatedModel.value : filteredModel.value
)

const recordsCount = computed(() => {
  if (serverMode.value && serverTotalResolved.value != null) {
    return serverTotalResolved.value
  }
  return filteredModel.value.reduce((acc, step) => acc + (step.changes?.length ? 1 : 0), 0)
})

watch(
  recordsCount,
  n => emit('update:recordsCount', n),
  { immediate: true }
)

/** Старое «удалено» без полей: четыре текста берём из снимка показания. */
function appendIndicationDeleteFields(ch, related) {
  if (ch?.model_name !== 'ProductIndications' || !Array.isArray(ch.changes)) {
    return
  }
  if (!ch.changes.some(item => item?.action === 'delete_object')) {
    return
  }
  const fields = ['short_text_ru', 'full_text_ru', 'short_text_en', 'full_text_en']
  if (ch.changes.some(item => fields.includes(String(item?.path?.[0] || '')))) {
    return
  }
  const bucket = related?.ProductIndications
  const snap = bucket && ch.model_id != null
    ? bucket[ch.model_id] ?? bucket[String(ch.model_id)]
    : null
  if (!snap || typeof snap !== 'object') {
    return
  }
  for (const field of fields) {
    const value = String(snap[field] || '').trim()
    if (!value) {
      continue
    }
    ch.changes.push({
      action: 'values_changed',
      path: [field],
      old_value: value,
      value: '',
    })
  }
}

/** Детали до/после для действий досье (имя, путь, версия, ACL) — в колонку «Действие». */
function formatDossierActionDetail(change) {
  const action = change?.action || ''
  const before = change?.before
  const after = change?.after
  if (action === 'dossier_rename') {
    const oldName = before?.name ?? ''
    const newName = after?.name ?? ''
    if (oldName || newName) {
      return escapeHtmlText(historyPhrase('dossier.renameDetail', { old: oldName, new: newName }))
    }
  }
  if (action === 'dossier_move') {
    const items = Array.isArray(before) ? before : [before]
    const afterItems = Array.isArray(after) ? after : [after]
    const parts = items.map((b, i) => {
      const a = afterItems[i] || {}
      return historyPhrase('dossier.moveDetail', {
        from: b?.path || '',
        to: a?.path || '',
      })
    })
    return escapeHtmlText(parts.filter(Boolean).join('; '))
  }
  if (action === 'dossier_upload_replace' || action === 'dossier_save') {
    const oldVer = formatDossierVersionLabel(before)
    const newVer = formatDossierVersionLabel(after)
    if (oldVer || newVer) {
      return escapeHtmlText(
        historyPhrase('dossier.versionDetail', {
          old: oldVer || '—',
          new: newVer || '—',
        })
      )
    }
  }
  if (action === 'dossier_acl_change') {
    return escapeHtmlText(formatDossierAclDetail(before, after))
  }
  return ''
}

/** Ключ субъекта ACL: user:<id> или role:<id|name>. */
function dossierAclSubjectKey(rule) {
  if (!rule || typeof rule !== 'object') {
    return ''
  }
  if (rule.user_id != null) {
    return `user:${rule.user_id}`
  }
  if (rule.role_id != null) {
    return `role:${rule.role_id}`
  }
  if (rule.role) {
    return `role:${rule.role}`
  }
  if (rule.username) {
    return `user:${rule.username}`
  }
  return ''
}

function dossierAclAccessLabel(access) {
  if (access === 'edit') {
    return historyPo('acl_edit')
  }
  if (access === 'read') {
    return historyPo('acl_read')
  }
  return String(access || '')
}

function dossierAclWhoLabel(rule) {
  if (!rule || typeof rule !== 'object') {
    return ''
  }
  if (rule.user_id != null || rule.username || rule.fio) {
    return String(rule.fio || rule.username || `#${rule.user_id}`).trim()
  }
  if (rule.role) {
    return getRoleCaption({ name: rule.role }, uiPrefersEnglish()) || String(rule.role)
  }
  return ''
}

/** Diff правил доступа: кто добавлен/снят/сменён уровень. */
function formatDossierAclDetail(before, after) {
  const beforeRules = Array.isArray(before?.rules) ? before.rules : []
  const afterRules = Array.isArray(after?.rules) ? after.rules : []
  const beforeMap = new Map()
  const afterMap = new Map()
  for (const rule of beforeRules) {
    const key = dossierAclSubjectKey(rule)
    if (key) {
      beforeMap.set(key, rule)
    }
  }
  for (const rule of afterRules) {
    const key = dossierAclSubjectKey(rule)
    if (key) {
      afterMap.set(key, rule)
    }
  }
  const parts = []
  for (const [key, rule] of afterMap) {
    const prev = beforeMap.get(key)
    const who = dossierAclWhoLabel(rule)
    if (!who) {
      continue
    }
    const nextAccess = dossierAclAccessLabel(rule.access)
    if (!prev) {
      parts.push(historyPhrase('dossier.aclAdded', { who, access: nextAccess }))
      continue
    }
    if (String(prev.access || '') !== String(rule.access || '')) {
      parts.push(
        historyPhrase('dossier.aclChanged', {
          who,
          old: dossierAclAccessLabel(prev.access),
          new: nextAccess,
        })
      )
    }
  }
  for (const [key, rule] of beforeMap) {
    if (afterMap.has(key)) {
      continue
    }
    const who = dossierAclWhoLabel(rule)
    if (!who) {
      continue
    }
    parts.push(
      historyPhrase('dossier.aclRemoved', {
        who,
        access: dossierAclAccessLabel(rule.access),
      })
    )
  }
  if (!parts.length) {
    // Нечего диффать (или старые записи без rules) — показать итоговый список.
    const listed = afterRules.length ? afterRules : beforeRules
    for (const rule of listed) {
      const who = dossierAclWhoLabel(rule)
      if (!who) {
        continue
      }
      parts.push(
        historyPhrase('dossier.aclGranted', {
          who,
          access: dossierAclAccessLabel(rule.access),
        })
      )
    }
  }
  return parts.join('; ')
}

/** Номер версии файла (из журнала; UUID не показываем). */
function formatDossierVersionLabel(snap) {
  if (!snap || typeof snap !== 'object') {
    return ''
  }
  const ver = snap.version
  if (ver != null && ver !== '' && Number(ver) > 0) {
    return String(ver)
  }
  return ''
}

function formatDiff(ch) {
  const chChanges = Array.isArray(ch?.changes) ? ch.changes : []
  appendIndicationDeleteFields(ch, this)
  if (isAuditTrail.value && auditTrailExcludedModels.has(ch.model_name)) {
    return { entity: null, key: `${ch.model_name}:${ch.model_id}`, changes: [] }
  }
  let rel = withCardSubject(
    resolveHistoryEntityRelated(this, ch.model_name, ch.model_id),
    ch.model_name,
    ch.model_id,
  )
  if (!rel && Array.isArray(this[ch.model_name])) {
    rel = this[ch.model_name].find(d => String(d.id) === String(ch.model_id))
  }
  const fmt = { entity: null, key: `${ch.model_name}:${ch.model_id}` }
  const changes = []

  switch (ch.model_name) {
    case 'Activities':
    case 'ActivityTypes':
    case 'Comments':
    case 'Consumables':
    case 'ConsumablesExpenses':
    case 'ConsumablesPurchases':
    case 'ConsumablesStock':
    case 'Devices':
    case 'DevicesExpenses':
    case 'DevicesPurchases':
    case 'DevicesStock':
    case 'MaterialsComm':
    case 'MaterialsCommExpenses':
    case 'MaterialsCommPurchases':
    case 'MaterialsCommStock':
    case 'MaterialsNd':
    case 'MaterialsNdExpenses':
    case 'MaterialsNdPurchases':
    case 'MaterialsNdStock':
    case 'Medicines':
    case 'MedicinesExpenses':
    case 'MedicinesPurchases':
    case 'MedicinesStock':
    case 'Products':
    case 'Submissions':
    case 'ProductManufacturers':
    case 'ProductDosageForms':
    case 'ProductDosageFormItems':
    case 'ProductAfsApis':
    case 'ProductAfsApiManufacturers':
    case 'ProductIndications':
    case 'GmpForSubmission':
    case 'GmpForSubmissionAction':
    case 'EaeuProductContacts':
    case 'Taxes':
    case 'ServiceNotes':
    case 'Sites':
    case 'GmpStatusList':
    case 'Events':
    case 'HistorySandbox':
    case 'MedicineTrademarks':
    case 'Orders':
    case 'Destructions':
    case 'Templates':
    case 'Uploads':
    case 'User':
    case 'Rsd':
    case 'AssignedUsers':
    case 'Vendors':
    case 'Dropdowns':
    case 'Dossier':
      if (ch.model_name.endsWith('Purchases')) {
        if (rel && rel.length) {
          rel = JSON.parse(rel)
        }
      }
      if (ch.model_name.endsWith('Destructions') || ch.model_name.endsWith('Expenses')) {
        if (rel && typeof rel === 'string') {
          rel = JSON.parse(rel)
        }
      }
      if (repeatingHistoryEntity(ch, this)) {
        fmt.entity = repeatingHistoryEntity(ch, this)
      } else if (models[ch.model_name]?.entityName) {
        fmt.entity = models[ch.model_name].entityName(ch, rel, isAuditTrail)
      } else {
        fmt.entity = historyPhrase('entity.generic', { model: ch.model_name, id: ch.model_id })
      }
      break
    case 'MedicineManufacturer':
      // related целиком: Manufacturers + MedicineManufacturer[medicineId]
      if (models[ch.model_name]?.entityName) {
        fmt.entity = models[ch.model_name].entityName(ch, this, isAuditTrail)
      } else {
        fmt.entity = historyPhrase('entity.generic', { model: ch.model_name, id: ch.model_id })
      }
      break
    case 'EmailAddress':
    case 'Communication':
    case 'Departments':
    case 'Laboratories':
    case 'WriteOffs':
    case 'Complaints':
    case 'Receptions':
    case 'ConsumablesDestructions':
    case 'DevicesDestructions':
    case 'MaterialsCommDestructions':
    case 'MaterialsNdDestructions':
    case 'MedicinesDestructions':
      if (models[ch.model_name]?.entityName) {
        fmt.entity = models[ch.model_name].entityName(ch, this, isAuditTrail)
      } else {
        fmt.entity = historyPhrase('entity.generic', { model: ch.model_name, id: ch.model_id })
      }
      break
    default:
      fmt.entity = historyPhrase('entity.generic', { model: ch.model_name, id: ch.model_id })
  }

  const cardEntity = resolveCardHistoryEntity(ch, this)
  if (cardEntity) {
    fmt.entity = cardEntity
  }

  const excludeFields = [
    'vortex_log',
    'part_id',
    'unique_id',
    'receiving_date_actual_vortex',
    'related_stocks',
    'assigned_users',
    'rtms_activity_id',
    'po_entry_id',
  ]

  // Проверяем, содержат ли все изменения только цифры в path (костыль для фильтрации)
  const hasOnlyNumericPaths =
    chChanges.length > 0 &&
    chChanges.every(change => {
      return (
        Array.isArray(change.path) &&
        change.path.length > 0 &&
        change.path.every(segment => /^\d+$/.test(String(segment)))
      )
    })

  // Если все изменения содержат цифры в path, пропускаем их
  if (hasOnlyNumericPaths) {
    return { entity: null, key: `${ch.model_name}:${ch.model_id}`, changes: [] }
  }

  const syntheticRows = buildSyntheticHistoryRows(ch, chChanges, this)
  const syntheticRowsByIndex = new Map()
  for (const row of syntheticRows) {
    if (!syntheticRowsByIndex.has(row.index)) {
      syntheticRowsByIndex.set(row.index, [])
    }
    syntheticRowsByIndex.get(row.index).push(row)
  }
  const syntheticFieldsToSkip = new Set(syntheticRows.flatMap(row => row.skipFields))
  // Заявки RTMS (rtms_applications) в истории подачи не показываем.
  if (ch.model_name === 'Submissions') {
    syntheticFieldsToSkip.add('rtms_applications')
  }

  const notifAcc = {
    added: new Set(),
    removed: new Set(),
  }

  for (const [changeIndex, change] of chChanges.entries()) {
    const changesBefore = changes.length
    try {
    normalizePackScalarHistoryChange(change)
    change.model_id = ch.model_id
    change.model_name = ch.model_name
    for (const syntheticRow of syntheticRowsByIndex.get(changeIndex) || []) {
      changes.push(syntheticRow.change)
    }
    if (syntheticFieldsToSkip.has(change.path?.[0])) {
      continue
    }
    // Старые записи второй стороны связи могли получить hist_key карточки Status.
    if (
      props.modelName === 'GmpStatusList'
      && ch.model_name === 'Sites'
      && change.action === 'site_gmp_status_linked'
    ) {
      continue
    }
    if (shouldSkipTaxesHistoryChange(ch, change, isAuditTrail.value)) {
      continue
    }
    if (shouldSkipProductsHistoryChange(ch, change, isAuditTrail.value)) {
      continue
    }
    if (shouldSkipSitesHistoryChange(ch, change)) {
      continue
    }
    if (shouldSkipMedicinesHistoryChange(ch, change)) {
      continue
    }
    if (shouldSkipUserFacsimileHistoryChange(ch.model_name, change)) {
      continue
    }
    if (shouldSkipProductRiskCategoryChange(ch, change)) {
      continue
    }
    if (shouldSkipProductCollectionBlob(ch, change)) {
      continue
    }
    if (shouldSkipHomeCardBlob(ch, change)) {
      continue
    }
    if (shouldSkipMirroredPackColumn(ch, change, chChanges)) {
      continue
    }
    // text дублирует краткое показание RUS, отдельной строкой его не пишем.
    if (
      ch.model_name === 'ProductIndications'
      && historyRelationField(change) === 'text'
      && (ch.changes || []).some(item =>
        ['short_text_ru', 'short_text_en'].includes(String(item?.path?.[0] || ''))
      )
    ) {
      continue
    }
    const cfmt = {}
    const iobj = this?.initial?.[ch.model_name]?.find(obj => obj.id === ch.model_id)
    const hasAmountStatus = iobj && 'amount' in iobj && 'status' in iobj
    const skipAmountStatus = iobj && iobj.amount === 0 && iobj.status?.value == null
    const exp_text =
      hasAmountStatus && ch.model_id !== props.modelId && !skipAmountStatus
        ? ` ${_get(fields, ['amount'])} ${iobj.amount}, ${_get(fields, ['status'])} ${iobj.status?.value}`
        : ''

    // Для Rollback сначала добавляем запись "отмена действия:"
    if (ch.comment === 'Rollback' && (change.action === 'values_changed' || change.action === 'type_changes')) {
      const rollbackHead = Array.isArray(change.path) ? change.path[0] : change.path
      if (rollbackHead == null || !excludeFields.includes(rollbackHead)) {
        const rollbackField =
          resolveRegistrationCertificatesFieldLabel(change) ||
          resolveProductCollectionFieldLabel(change) ||
          resolveHomeCardFieldLabel(change) ||
          _get(fields, [ch.model_name, ...change.path]) ||
          _get(fields, change.path)
        if (!(rollbackField instanceof Function)) {
          let fieldName = rollbackField || change.path.join('.')
          if (fieldName == change.path?.[0]) {
            const req_field = searchFieldset(this, fieldName)
            if (req_field) {
              fieldName = req_field
            }
          }

          const val = getVal(ch, change, this, 'value')
          const oldVal = getVal(ch, change, this, 'old_value')
          if (oldVal) {
            const valHtml = val ? DOMPurify.sanitize(`<span class="text-success d-inline-block">${val}</span>`) : ''
            const oldValHtml = DOMPurify.sanitize(`<span class="text-danger d-inline-block">${oldVal}</span>`)
            const rollbackText = val
              ? historyPhrase('actions.rollbackTextTo', { field: fieldName, from: valHtml, to: oldValHtml })
              : historyPhrase('actions.rollbackText', { field: fieldName, to: oldValHtml })
            changes.push({
              action: historyAction('rollbackCancel'),
              text: rollbackText
            })
          }
        }
      }
    }

    switch (change.action) {
      case 'values_changed':
      case 'type_changes':
      case 'exchanged':
      case 'active_change':
        if (
          (Array.isArray(change.path) ? change.path[0] : change.path) != null
          && excludeFields.includes(Array.isArray(change.path) ? change.path[0] : change.path)
        ) continue
        if (ch.model_name === 'GmpForSubmissionAction' && change.path?.[0] === 'sort') continue
        // sort у площадки — номер строки в списке, не текст выбранного дропдауна.
        if (ch.model_name === 'ProductManufacturers' && historyRelationField(change) === 'sort') continue

        {
          const ruLabel = resolveRegistrationCertificatesFieldLabel(change)
            || resolveProductCollectionFieldLabel(change)
            || resolveHomeCardFieldLabel(change)
          if (ruLabel) {
            cfmt.field = ruLabel
          } else {
            const fieldPath = historyFieldPath(change)
            cfmt.field =
              (fieldPath.length
                ? _get(fields, [ch.model_name, ...fieldPath]) || _get(fields, fieldPath)
                : _get(fields, [ch.model_name, change.path]) || _get(fields, change.path))

            if (cfmt.field && typeof cfmt.field === 'object' && !(cfmt.field instanceof Function)) {
              cfmt.field = cfmt.field.label || cfmt.field.title || null
            }

            // Позволяет моделям динамически определять название поля (например, для добавления префикса группы)
            if (models[ch.model_name]?.fieldName) {
              cfmt.field = models[ch.model_name].fieldName(ch, change, cfmt.field)
            }
            if (!(cfmt.field instanceof Function) && cfmt.field && typeof cfmt.field !== 'object') {
              // Для ProductManufacturers group уже вшит в fieldName — не затирать плоским ключом.
              if (!(ch.model_name === 'ProductManufacturers' && change.group)) {
                const fieldKey = historyFieldPath(change)
                const leaf = fieldKey.length ? String(fieldKey[fieldKey.length - 1]) : String(change.path?.[0] ?? '')
                const fullKey = change.group && leaf
                  ? `${change.group}.${leaf}`
                  : fieldKey.length
                    ? fieldKey.join('.')
                    : leaf
                cfmt.field = historyFieldLabel(ch.model_name, fullKey, cfmt.field)
              }
            }
          }
        }
        if (!(cfmt.field instanceof Function)) {
          if (ch.model_name === 'User') {
            const userLabel = userHistoryFieldLabel(change.path)
            if (userLabel) {
              cfmt.field = userLabel
            }
          }
          if (!cfmt.field || (typeof cfmt.field === 'object' && cfmt.field !== null)) {
            const fieldKey = historyFieldPath(change)
            const leaf = fieldKey.length ? String(fieldKey[fieldKey.length - 1]) : ''
            const fullKey = change.group && leaf
              ? `${change.group}.${leaf}`
              : fieldKey.length
                ? fieldKey.join('.')
                : String(change.path?.join?.('.') ?? '')
            cfmt.field =
              historyFieldLabel(ch.model_name, fullKey, null)
              || fullKey
              || (Array.isArray(change.path) ? change.path.join('.') : '')
          }

          if (!change.relation) {
            change.relation = findClass(
              this?.map || [],
              ch.model_name,
              historyRelationField(change)
            )
          }
          /* Площадка производителя → Sites (иначе в деталях остаётся id) */
          if (
            !change.relation &&
            ch.model_name === 'ProductManufacturers' &&
            ['site_id', 'site'].includes(historyRelationField(change))
          ) {
            change.relation = 'Sites'
          }

          cfmt.action = historyAction('changed')
          // getVal меняет change.value / old_value, сырой текст снимаем заранее.
          const rawNext = historyStoredText(change.value)
          const rawPrev = historyStoredText(change.old_value)
          let nextVal = ''
          let prevVal = ''
          try {
            nextVal = getVal(ch, change, this)
            prevVal = getVal(ch, change, this, 'old_value')
          } catch (err) {
            console.warn('[history] field value format failed', ch.model_name, change.path, err)
            nextVal = rawNext
            prevVal = rawPrev
          }
          // Форматтер иногда отдаёт пусто или одну и ту же подпись на оба конца,
          // хотя в History значения разные. Тогда показываем как записано.
          if (!historyStoredText(nextVal) && rawNext) {
            nextVal = rawNext
          }
          if (!historyStoredText(prevVal) && rawPrev) {
            prevVal = rawPrev
          }
          const nextText = historyStoredText(nextVal)
          const prevText = historyStoredText(prevVal)
          /* Одинаковая подпись — не строка. Пустой форматтер при разных сырых значениях оставляем. */
          if (nextText === prevText && (nextText !== '' || rawNext === rawPrev)) {
            continue
          }
          cfmt.val = DOMPurify.sanitize(
            `<span class="text-success d-inline-block">${nextVal}</span>`
          )
          cfmt.old_val = DOMPurify.sanitize(
            `<span class="text-danger d-inline-block">${prevVal}</span>`
          )
          // Сохранение не меняло день, в истории обе стороны одной датой.
          if (
            ch.model_name === 'Submissions'
            && isSameDisplayedHistoryDate(cfmt.old_val, cfmt.val)
          ) {
            continue
          }
        }

        break
      case 'add_object':
        cfmt.action = historyAction('addedWithDetails', { details: exp_text })
        if (ch.model_name === 'ProductDosageFormItems') {
          const block = strengthBlockLabel(this, ch.model_id)
          if (block) {
            cfmt.action = historyAction('added')
            cfmt.field = () => block
          }
        }
        if (ch.model_name === 'ProductIndications') {
          cfmt.action = historyAction('added')
          cfmt.field = () => ''
        }
        if (ch.model_name === 'GmpForSubmissionAction') {
          cfmt.field = () => DOMPurify.sanitize(`${t('col.action')}: ${gmpActionDetailsLabel(ch, rel, this, 'text-success')}`)
        }
        if (ch.model_name === 'AssignedUsers') {
          cfmt.field = _get(fields, [ch.model_name, 'assigned_user'])
        }
        if (ch.model_name === 'Dropdowns') {
          cfmt.field = () => DOMPurify.sanitize(formatDropdownsHistoryDetails(change))
        }
        if (ch.model_name === 'Uploads' && isEntityDocumentHistoryEntry(ch)) {
          const div = change.division || ch.changes?.[0]?.division
          const docLabel = entityDocumentDivisionLabel(div)
          cfmt.action = historyAction('added')
          cfmt.field = (_, related) =>
            DOMPurify.sanitize(
              `${docLabel} ${linkifyEntityUploadFromChange(related, change.upload_id ?? ch.model_id, change)}`
            )
        }
        break
      case 'delete_object':
        cfmt.action = historyAction('deleted')
        if (ch.model_name === 'ProductDosageFormItems') {
          const block = strengthBlockLabel(this, ch.model_id)
          cfmt.field = () => block || historyPhrase('product.strength', { id: ch.model_id })
        }
        if (ch.model_name === 'ProductIndications') {
          cfmt.field = () => ''
        }
        if (ch.model_name === 'GmpForSubmissionAction') {
          cfmt.field = () => DOMPurify.sanitize(`${t('col.action')}: ${gmpActionDetailsLabel(ch, rel, this, 'text-danger')}`)
        }
        if (ch.model_name === 'Dropdowns') {
          cfmt.field = () => DOMPurify.sanitize(formatDropdownsHistoryDetails(change))
        }
        if (ch.model_name === 'Uploads' && isEntityDocumentHistoryEntry(ch)) {
          const div = change.division || ch.changes?.[0]?.division
          const docLabel = entityDocumentDivisionLabel(div)
          cfmt.field = (_, related) =>
            DOMPurify.sanitize(
              historyPhrase('actions.documentRemoved', {
                label: docLabel,
              }) + ` ${linkifyEntityUploadFromChange(related, change.upload_id ?? ch.model_id, change)}`
            )
        } else if (ch.model_name === 'Uploads') {
          cfmt.customUpload = buildUploadForViewer(ch, change, rel)
        }
        if (
          props.modelName
          && ch.model_name !== props.modelName
          && ch.model_name !== 'ProductIndications'
          && !(cfmt.field instanceof Function)
          && !cfmt.customUpload
        ) {
          const child = deletedSubjectLabel(ch, this)
          if (child) {
            cfmt.field = () => child
          }
        }
        break
      case 'iterable_item_removed':
      case 'iterable_item_added':
      case 'dictionary_item_added': {
        const pathRoot = historyIterablePathRoot(change)
        if (ch.model_name === 'User' && !USER_ITERABLE_HISTORY_FIELDS.includes(pathRoot)) {
          continue
        }
        if (!change.relation) {
          change.relation = findClass(
            this?.map || [],
            ch.model_name,
            pathRoot || ''
          )
        }
        let ival = String(getVal(ch, change, this) || '').trim()
        try {
          const parsed = JSON.parse(ival)
          if (Array.isArray(parsed)) {
            ival = ''
          }
        } catch {
          /* keep ival */
        }
        if (isHistoryValueEmpty(ival)) {
          continue
        }
        if (ch.model_name === 'User' && pathRoot === 'notification_settings') {
          const notifLabel =
            formatNotificationSettingHistoryLabel(ival) || historyKnownLabel(ival)
          if (notifLabel) {
            if (
              change.action === 'iterable_item_removed'
              || change.action === 'dictionary_item_removed'
            ) {
              notifAcc.removed.add(notifLabel)
            } else {
              notifAcc.added.add(notifLabel)
            }
          }
          continue
        }
        if (pathRoot === 'roles') {
          ival = historyKnownLabel(ival)
        }
        cfmt.iterable = true
        {
          const ruLabel = resolveRegistrationCertificatesFieldLabel(change)
            || resolveHomeCardFieldLabel(change)
          if (ruLabel) {
            cfmt.field = ruLabel
          } else {
            const fieldPath = historyFieldPath(change)
            cfmt.field =
              (fieldPath.length
                ? _get(fields, [ch.model_name, ...fieldPath]) || _get(fields, fieldPath)
                : _get(fields, [ch.model_name, change.path]) || _get(fields, change.path))

            if (cfmt.field && typeof cfmt.field === 'object' && !(cfmt.field instanceof Function)) {
              cfmt.field = cfmt.field.label || cfmt.field.title || null
            }

            if (models[ch.model_name]?.fieldName) {
              cfmt.field = models[ch.model_name].fieldName(ch, change, cfmt.field)
            }
            if (
              !(cfmt.field instanceof Function)
              && cfmt.field
              && !(ch.model_name === 'ProductManufacturers' && change.group)
            ) {
              const fieldKey = historyFieldPath(change)
              const leaf = fieldKey.length ? String(fieldKey[fieldKey.length - 1]) : String(change.path?.[0] ?? '')
              const fullKey = change.group && leaf
                ? `${change.group}.${leaf}`
                : fieldKey.length
                  ? fieldKey.join('.')
                  : leaf
              cfmt.field = historyFieldLabel(ch.model_name, fullKey, cfmt.field)
            }
          }
        }
        const iterableValueClass =
          change.action === 'iterable_item_removed' ? 'text-danger' : 'text-success'
        cfmt.val = DOMPurify.sanitize(
          `<span class="${iterableValueClass} d-inline-block">${escapeHtmlText(ival)}</span>`
        )
        if (change.action == 'iterable_item_removed') {
          cfmt.action = historyAction('removedItems')
        } else {
          cfmt.action = historyAction('addedItems')
        }
        break
      }
      case 'file_added':
        cfmt.action = historyAction('fileAdded')
        break
      case 'stock_dispatched':
        cfmt.action = historyAction('dispatched')
        break
      case 'stock_destructed':
        cfmt.action = historyAction('destructed')
        break
      case 'declined':
        if (ch.model_name !== 'OfficialReplies')
          cfmt.action = historyAction('declined')
        break
      case 'approved':
        cfmt.action = historyAction('approved')
        break
      case 'password_change':
        cfmt.action = historyAction('passwordChanged')
        break
      case 'closed':
        cfmt.action = historyAction('closed')
        break
      case 'approve_request':
        cfmt.action = historyAction('approveRequest')
        break
      case 'comment_edited':
        cfmt.action = historyAction('commentEdited')
        cfmt.field = () => DOMPurify.sanitize(formatCommentHistoryDetails(ch.comment, 'edited'))
        break
      case 'comment_added':
        cfmt.action = historyAction('commentAdded')
        cfmt.field = () => DOMPurify.sanitize(formatCommentHistoryDetails(ch.comment, 'added'))
        break
      case 'comment_deleted':
        cfmt.action = historyAction('commentDeleted')
        cfmt.field = () => DOMPurify.sanitize(formatCommentHistoryDetails(ch.comment, 'deleted'))
        break
      case 'archived':
        cfmt.action = historyAction('archived')
        break
      case 'add_suborder':
        cfmt.action = historySuborderAction(false, change.value)
        break
      case 'remove_suborder':
        cfmt.action = historySuborderAction(true, change.value)
        break
      case 'tax_request_created': {
        const statusLabel = resolveTaxStatusHistoryLabel(change)
        const taxId = change.tax_id ?? change.value
        const linkText = historyPhrase('submissions.taxRequestCreated', { status: statusLabel })
        cfmt.action = ''
        cfmt.field = () =>
          DOMPurify.sanitize(`<a href="/taxes/${taxId}">${linkText}</a>`)
        break
      }
      case 'tax_linked': {
        const taxId = change.tax_id ?? change.value
        const idText = escapeHtmlText(String(taxId ?? ''))
        cfmt.action = ''
        cfmt.field = () =>
          DOMPurify.sanitize(
            `${escapeHtmlText(historyPhrase('submissions.taxLinked'))}<a href="/taxes/${idText}">${idText}</a>`
          )
        break
      }
      case 'tax_unlinked': {
        const taxId = change.tax_id ?? change.old_value
        const idText = escapeHtmlText(String(taxId ?? ''))
        cfmt.action = ''
        cfmt.field = () =>
          DOMPurify.sanitize(
            `${escapeHtmlText(historyPhrase('submissions.taxUnlinked'))}<a href="/taxes/${idText}">${idText}</a>`
          )
        break
      }
      case 'submission_linked': {
        const submissionId = change.submission_id ?? change.value
        // В деталях — номер подачи (Подача 123), как в ТЗ.
        const link = historyEntityLink('submissions', submissionId, submissionId)
        cfmt.action = historyAction('connectionAdded')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('products.submissionLinked', { link }))
        break
      }
      case 'created_from_product': {
        const productId = change.product_id ?? change.value
        const label = change.product_label || productId
        const link = historyEntityLink('products', productId, label)
        cfmt.action = historyAction('createdOnBasis')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('submissions.createdFromProduct', { link }))
        break
      }
      case 'site_gmp_status_linked': {
        const id = change.gmp_status_id ?? change.value
        const label = change.gmp_status_label || id
        const link = historyEntityLink('gmp_status_list', id, label)
        cfmt.action = historyAction('connectionAdded')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('sites.gmpStatusLinked', { link }))
        break
      }
      case 'gmp_status_site_linked': {
        const id = change.site_id ?? change.value
        const label = change.site_label || id
        const link = historyEntityLink('sites', id, label)
        cfmt.action = historyAction('connectionAdded')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('gmpStatus.siteLinked', { link }))
        break
      }
      case 'service_note_tax_linked': {
        const id = change.tax_id ?? change.value
        const link = historyEntityLink('taxes', id, id)
        cfmt.action = historyAction('connectionAdded')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('serviceNotes.taxLinked', { link }))
        break
      }
      case 'tax_service_note_linked': {
        const id = change.service_note_id ?? change.value
        const link = historyEntityLink('documents/service-notes', id, id)
        cfmt.action = historyAction('connectionAdded')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('taxes.serviceNoteLinked', { link }))
        break
      }
      case 'tax_service_note_deleted': {
        const id = change.service_note_id ?? change.old_value
        const link = historyEntityLink('documents/service-notes', id, id)
        cfmt.action = historyAction('deleted')
        cfmt.field = () =>
          DOMPurify.sanitize(historyPhrase('taxes.serviceNoteDeleted', { link }))
        break
      }
      case 'tax_accountant_notified': {
        cfmt.action = historyAction('accountantNotified')
        // Пустой field-функция не даёт отбросить строку: текст только в колонке действия.
        cfmt.field = () => ''
        break
      }
      case 'site_status_certificate_uploaded': {
        const statusId = change.gmp_status_id ?? change.value
        const statusLabel = change.gmp_status_label || statusId
        cfmt.action = historyAction('added')
        cfmt.field = (_, related) => {
          const file = linkifyEntityUploadFromChange(related, change.upload_id, change)
          const status = historyEntityLink('gmp_status_list', statusId, statusLabel)
          return DOMPurify.sanitize(
            historyPhrase('sites.statusCertificateAddedToStatus', { file, status })
          )
        }
        break
      }
      case 'agreement_form_created': {
        cfmt.action = historyAction('added')
        cfmt.field = (_, related) => {
          const file = linkifyEntityUploadFromChange(related, change.upload_id, change)
          const label = historyPhrase('submissions.agreementFormAdded')
          return DOMPurify.sanitize(`${label} ${file}`.trim())
        }
        break
      }
      case 'agreement_sign_method': {
        cfmt.action = historyAction('selected')
        cfmt.field = (_, related) => {
          const file = linkifyEntityUploadFromChange(related, change.upload_id, change)
          const label = historyPhrase(
            change.mode === 'print'
              ? 'submissions.agreementSignMethodPrint'
              : 'submissions.agreementSignMethodElectronic'
          )
          return DOMPurify.sanitize(`${label} ${file}`.trim())
        }
        break
      }
      case 'agreement_scan_uploaded': {
        cfmt.action = historyAction('added')
        cfmt.field = (_, related) => {
          const file = linkifyEntityUploadFromChange(related, change.upload_id, change)
          const label = historyPhrase('submissions.agreementScanAdded')
          return DOMPurify.sanitize(`${label} ${file}`.trim())
        }
        break
      }
      case 'agreement_form_deleted': {
        cfmt.action = historyAction('agreementFormDeleted')
        break
      }
      case 'tax_document_replaced':
      case 'tax_document_added':
      case 'product_document_replaced':
      case 'product_document_added':
      case 'service_note_document_replaced':
      case 'service_note_document_added':
      case 'service_note_document_deleted':
      case 'tax_document_deleted':
      case 'template_file_replaced':
      case 'template_file_added':
      case 'submission_document_replaced':
      case 'submission_document_added':
      case 'submission_document_deleted':
      case 'site_document_replaced':
      case 'site_document_added':
      case 'gmp_status_document_replaced':
      case 'gmp_status_document_added':
        if (
          ch.model_name === 'Taxes' ||
          ch.model_name === 'Products' ||
          ch.model_name === 'ServiceNotes' ||
          ch.model_name === 'Templates' ||
          ch.model_name === 'Submissions' ||
          ch.model_name === 'Sites' ||
          ch.model_name === 'GmpStatusList'
        ) {
          const div = change.division || change.path?.[0]
          let docLabel = entityDocumentDivisionLabel(div)
          if (
            (ch.model_name === 'ServiceNotes' || ch.model_name === 'Taxes')
            && div === 'service_note'
          ) {
            docLabel = fields.ServiceNotes?.scan || t('serviceNoteScan')
          }
          if (ch.model_name === 'ServiceNotes' && div === 'service_note_document') {
            docLabel = fields.ServiceNotes?.document || t('common.serviceNote')
          }
          if (div === 'sample_cover_letter') {
            // Для первого формирования показываем «добавлено», для повторного — «обновлено».
            cfmt.action = isEntityDocumentAddedAction(change.action)
              ? historyAction('added')
              : historyAction('updated')
            cfmt.field = (_, related) =>
              DOMPurify.sanitize(
                `${docLabel} ${linkifyEntityUploadFromChange(related, change.upload_id, change)}`
              )
          } else if (isEntityDocumentDeletedAction(change.action)) {
            cfmt.action = historyAction('deleted')
            cfmt.field = (_, related) =>
              DOMPurify.sanitize(
                `${docLabel} ${linkifyEntityUploadFromChange(related, change.upload_id, change)}`
              )
          } else if (ch.model_name === 'ServiceNotes' && div === 'service_note') {
            cfmt.action = isEntityDocumentAddedAction(change.action)
              ? historyAction('added')
              : historyAction('replaced')
            cfmt.field = (_, related) => {
              const file = linkifyEntityUploadFromChange(related, change.upload_id, change)
              const note = historyEntityLink('documents/service-notes', ch.model_id, ch.model_id)
              return DOMPurify.sanitize(
                historyPhrase('serviceNotes.scanAddedToCard', { file, note })
              )
            }
          } else if (isEntityDocumentAddedAction(change.action)) {
            cfmt.action = historyAction('added')
            cfmt.field = (_, related) =>
              DOMPurify.sanitize(
                `${docLabel} ${linkifyEntityUploadFromChange(related, change.upload_id, change)}`
              )
          } else {
            cfmt.action = historyAction('replaced')
            cfmt.field = (_, related) => {
              const oldLink = linkifyEntityUploadFromChange(related, change.old_upload_id, {
                ...change,
                filename: change.old_value,
                secure_filename: change.old_secure_filename,
              })
              const newLink = linkifyEntityUploadFromChange(related, change.upload_id, change)
              return DOMPurify.sanitize(
                historyPhrase('actions.documentReplaced', {
                  label: docLabel,
                  old: oldLink || '—',
                  new: newLink || '—',
                })
              )
            }
          }
        }
        break
      default:
        if (typeof change.action === 'string' && change.action.startsWith('dossier_')) {
          cfmt.action = historyAction(change.action)
          const detail = formatDossierActionDetail(change)
          if (detail) {
            cfmt.field = () => DOMPurify.sanitize(detail)
          }
        } else {
          cfmt.action = ''
        }
    }

    // Covered dosage form/stage: NULL и [] — одно и то же, действия не было.
    if (
      ch.model_name === 'GmpStatusList'
      && normalizeHistoryPath(change.path)[0] === 'covered_dosage_form_stage'
      && ['values_changed', 'type_changes', 'exchanged'].includes(change.action)
      && !(cfmt.field instanceof Function)
      && historyVisibleText(cfmt.old_val) === historyVisibleText(cfmt.val)
    ) {
      continue
    }

    const str = []
    const subjectName = restrictedAccessSubjectName(ch.model_name, change)
    if (subjectName) str.push(escapeHtmlText(subjectName))

    if (cfmt.field instanceof Function) {
      if (!change.relation) {
        change.relation = findClass(this?.map || [], ch.model_name, historyRelationField(change))
      }
      const _str = cfmt.field(change, this)
      if (_str) {
        str.push(_str)
      }
    } else {
      //str.push(cfmt.action)
      if (cfmt.field) {
        if (cfmt.field == change.path?.[0]) {
          const req_field = searchFieldset(this, cfmt.field)
          if (req_field) {
            cfmt.field = req_field
          }
        }
        const facsimileHistoryFieldLabel =
          typeof cfmt.field === 'string'
          && (cfmt.field.includes('": "') || /^"Факсимиле /.test(cfmt.field))
        str.push(facsimileHistoryFieldLabel ? cfmt.field : `"${cfmt.field}"`)
      }
      if (historyVisibleText(cfmt.old_val)) {
        str.push(`${historyPhrase('connectors.from')} ${cfmt.old_val}`)
      }
      if (cfmt.val) {
        if (cfmt.iterable) {
          str.push('- ' + toValue(cfmt.val))
        } else {
          str.push(`${historyPhrase('connectors.to')} ${toValue(cfmt.val)}`)
        }
      } else if (
        cfmt.old_val
        && !cfmt.iterable
        && !(
          ch.model_name === 'ProductIndications'
          && (ch.changes || []).some(item => item?.action === 'delete_object')
        )
      ) {
        str.push(`${historyPhrase('connectors.to')} \u00a0`)
      }
      const gmpStatusId = Number(change.gmp_status_id)
      if (Number.isFinite(gmpStatusId) && gmpStatusId > 0) {
        const link = `<a href="/gmp_status_list/${gmpStatusId}">${gmpStatusId}</a>`
        str.push(historyPhrase('connectors.inGmpStatus', { link }))
      }
    }

    const changeText = str.join(' ').trim()
    if (isFacsimileBinaryHistoryText(changeText)) {
      continue
    }
    // add_object/delete_object и dossier_*: часто без path/value — текст пустой,
    // но action («добавлено» / «загружен файл») — единственный смысл строки.
    // Иначе записи есть в API (Записей: N), а в модалке «История отсутствует».
    const actionOnlyOk =
      change.action === 'add_object'
      || change.action === 'delete_object'
      || (typeof change.action === 'string' && change.action.startsWith('dossier_'))
    if (
      isHistoryChangeTextEmpty(changeText)
      && !(cfmt.field instanceof Function)
      && !cfmt.customUpload
      && !actionOnlyOk
    ) {
      continue
    }

    if (cfmt.customUpload) {
      changes.push({ action: cfmt.action, text: '', upload: cfmt.customUpload })
      continue
    }
    changes.push({ action: cfmt.action, text: changeText })
    } catch (err) {
      console.warn('[history] change format failed', ch.model_name, change?.path, change?.action, err)
      if (changes.length === changesBefore) {
        changes.push(...fallbackFieldChanges({ ...ch, changes: [change] }, this))
      }
    }
  }

  if (ch.model_name === 'User') {
    const notifLabel =
      _get(fields, [ch.model_name, 'notification_settings'])
      || _get(fields, 'notification_settings')
      || t('common.notificationSettings')
    if (notifAcc.removed.size) {
      changes.push({
        action: historyAction('removedItems'),
        text: `"${notifLabel}" - ${[...notifAcc.removed].sort().join(', ')}`,
      })
    }
    if (notifAcc.added.size) {
      changes.push({
        action: historyAction('addedItems'),
        text: `"${notifLabel}" - ${[...notifAcc.added].sort().join(', ')}`,
      })
    }
  }

  fmt.entityUpload = changes.find(c => c.upload)?.upload || null
  fmt.changes = dropMirroredPackSizeHistoryLines(changes)
  return fmt
}

/**
 * Плоская колонка pack_size склеивает чипы всех блоков через «, ».
 * В истории это выглядит как правка первого блока: к его старому тексту
 * дописывается чип, который на самом деле появился только в следующем блоке.
 */
function dropMirroredPackSizeHistoryLines(changes) {
  const parsed = (changes || []).map(change => ({
    change,
    info: parsePackSizeHistoryLine(change?.text),
  }))
  const byLeaf = new Map()
  for (const item of parsed) {
    if (!item.info) continue
    const list = byLeaf.get(item.info.leaf) || []
    list.push(item.info)
    byLeaf.set(item.info.leaf, list)
  }
  const hidden = new Set()
  for (const item of parsed) {
    const info = item.info
    if (!info?.oldText) continue
    const later = (byLeaf.get(info.leaf) || [])
      .filter(other => other.n > info.n && other.newText)
      .sort((a, b) => a.n - b.n)
    if (!later.length) continue
    const mirrored = [info.oldText, ...later.map(other => other.newText)].join(', ')
    if (info.newText === mirrored) {
      hidden.add(item.change)
    }
  }
  // «Pack size ENG 1» на «A, B, C»: в первое поле попала склейка всех блоков.
  // Строка своего поля (чип дописали к старому тексту) остаётся.
  for (const item of parsed) {
    const info = item.info
    if (!info || hidden.has(item.change)) continue
    const segments = info.newText.split(', ').map(part => part.trim()).filter(Boolean)
    if (segments.length < 2) continue
    if (info.oldText && (info.newText === info.oldText || info.newText.startsWith(`${info.oldText}, `))) {
      continue
    }
    const others = (byLeaf.get(info.leaf) || []).filter(other => other.n !== info.n)
    const mentionsOther = others.some(other => {
      const foreign = [other.oldText, other.newText]
        .flatMap(text => String(text || '').split(', ').map(part => part.trim()).filter(Boolean))
      return foreign.some(part => segments.includes(part))
    })
    if (mentionsOther) {
      hidden.add(item.change)
    }
  }
  if (!hidden.size) {
    return changes
  }
  return changes.filter(change => !hidden.has(change))
}

function parsePackSizeHistoryLine(text) {
  const source = String(text || '')
  const label = source.match(/"(Pack size ENG|Pack size RUS) №?(\d+)"/)
  if (!label) {
    return null
  }
  const newText = historySpanText(source, 'text-success')
  if (!newText) {
    return null
  }
  return {
    leaf: label[1],
    n: Number(label[2]),
    oldText: historySpanText(source, 'text-danger'),
    newText,
  }
}

function historySpanText(html, className) {
  const match = String(html || '').match(new RegExp(`<span[^>]*\\b${className}\\b[^>]*>([\\s\\S]*?)</span>`))
  if (!match) {
    return ''
  }
  return decodeHistorySpan(match[1])
}

function decodeHistorySpan(text) {
  return String(text || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .trim()
}

function buildSyntheticHistoryRows(ch, chChanges, rel) {
  return [
    ...buildInspectionDatesRangeRows(ch, chChanges, rel),
    ...buildManualFieldRows(ch, chChanges, rel),
    ...buildEaeuProductAutofillRows(ch, chChanges, rel),
    ...buildProductRiskCategoryRows(ch, chChanges, rel),
    ...buildProductCollectionRows(ch, chChanges),
    ...buildHomeCardHistoryRows(ch, chChanges),
  ]
}

/** Объединяет изменения начала и конца периода инспекции в одну строку истории. */
function buildInspectionDatesRangeRows(ch, chChanges, rel) {
  if (ch.model_name !== 'GmpStatusList') return []

  const entries = chChanges
    .map((change, index) => ({ change, index }))
    .filter(({ change }) => normalizeHistoryPath(change.path)[0] === 'inspections_dates')
  if (!entries.length) return []

  const oldValue = formatInspectionDatesRange(ch, rel, entries, 'old_value')
  const value = formatInspectionDatesRange(ch, rel, entries, 'value')
  const row = buildSyntheticFieldChangedRow({
    label: _get(fields, [ch.model_name, 'inspections_dates']) || 'Inspections dates',
    oldValue,
    value,
    index: Math.min(...entries.map(entry => entry.index)),
    skipFields: ['inspections_dates'],
  })
  return row ? [row] : []
}

function formatInspectionDatesRange(ch, rel, entries, prop) {
  const rootEntry = entries.find(({ change }) => normalizeHistoryPath(change.path).length === 1)
  let raw = rootEntry?.change?.[prop]

  if (raw == null) {
    const indexedValues = []
    for (const { change } of entries) {
      const path = normalizeHistoryPath(change.path)
      const index = Number(path[1])
      if (Number.isInteger(index) && prop in change) {
        indexedValues[index] = change[prop]
      }
    }
    raw = indexedValues.filter(value => value != null && value !== '')
  }

  if (!raw || (Array.isArray(raw) && !raw.length)) return ''
  return getHistoryValue(
    ch,
    { path: ['inspections_dates'], [prop]: raw },
    rel,
    prop,
  )
}

function buildManualFieldRows(ch, chChanges, rel) {
  const groups = manualFieldGroups(ch.model_name)
  if (!groups.length) return []

  return groups
    .map(group => buildManualFieldRow(ch, chChanges, rel, group))
    .filter(Boolean)
}

function manualFieldGroups(modelName) {
  if (modelName === 'GmpForSubmission') {
    return [
      { field: 'product', manual: 'product_manual', manualValue: 'product_manual_value' },
      { field: 'bdp_site', manual: 'bdp_site_manual', manualValue: 'bdp_site_manual_value' },
      {
        field: 'batch_release_site',
        manual: 'batch_release_site_manual',
        manualValue: 'batch_release_site_manual_value',
      },
    ]
  }
  if (modelName === 'EaeuProductContacts') {
    return [
      {
        field: 'ra_cmc_user',
        labelField: 'ra_cmc',
        manual: 'ra_cmc_manual',
        manualValue: 'ra_cmc_manual_value',
      },
      { field: 'ra_pil_user', labelField: 'ra_pil', manual: 'ra_pil_manual', manualValue: 'ra_pil_manual_value' },
      {
        field: 'ra_lead_user',
        labelField: 'ra_lead',
        manual: 'ra_lead_manual',
        manualValue: 'ra_lead_manual_value',
      },
      {
        field: 'medical_lead_user',
        labelField: 'medical_lead',
        manual: 'medical_lead_manual',
        manualValue: 'medical_lead_manual_value',
      },
    ]
  }
  return []
}

function buildManualFieldRow(ch, chChanges, rel, group) {
  const indexed = Object.fromEntries(
    chChanges
      .map((change, index) => [change.path?.[0], { change, index }])
      .filter(([field]) => field)
  )
  const fieldEntry = indexed[group.field]
  const manualEntry = indexed[group.manual]
  const manualValueEntry = indexed[group.manualValue]

  if (!manualEntry && !manualValueEntry) {
    return null
  }

  const oldManual = parseHistoryBool(manualEntry?.change?.old_value)
  const newManual = parseHistoryBool(manualEntry?.change?.value)
  const oldValue = getManualDisplayValue(ch, rel, {
    fieldChange: fieldEntry?.change,
    manualValueChange: manualValueEntry?.change,
    prop: 'old_value',
    manual: oldManual,
  })
  const value = getManualDisplayValue(ch, rel, {
    fieldChange: fieldEntry?.change,
    manualValueChange: manualValueEntry?.change,
    prop: 'value',
    manual: newManual,
  })

  if (!oldValue && !value) {
    return null
  }

  const fieldKey = group.labelField || group.field
  const fallback = _get(fields, [ch.model_name, fieldKey]) || fieldKey
  const label = historyFieldLabel(ch.model_name, fieldKey, typeof fallback === 'string' ? fallback : fieldKey)
  const text = [`"${label}"`]
  if (oldValue) {
    text.push(`${historyPhrase('connectors.from')} ${historyValueHtml(oldValue, 'text-danger')}`)
  }
  if (value) {
    text.push(`${historyPhrase('connectors.to')} ${historyValueHtml(value, 'text-success')}`)
  }

  return {
    index: Math.min(...[fieldEntry?.index, manualEntry?.index, manualValueEntry?.index].filter(Number.isInteger)),
    skipFields: [group.field, group.manualValue],
    change: {
      action: historyAction('changed'),
      text: text.join(' '),
    },
  }
}

function getManualDisplayValue(ch, rel, { fieldChange, manualValueChange, prop, manual }) {
  if (manual === true) {
    return getHistoryValue(ch, manualValueChange, rel, prop) || getHistoryValue(ch, fieldChange, rel, prop)
  }
  if (manual === false) {
    return getHistoryValue(ch, fieldChange, rel, prop) || getHistoryValue(ch, manualValueChange, rel, prop)
  }
  return getHistoryValue(ch, manualValueChange, rel, prop) || getHistoryValue(ch, fieldChange, rel, prop)
}

function buildEaeuProductAutofillRows(ch, chChanges, rel) {
  if (ch.model_name !== 'EaeuProductContacts') {
    return []
  }
  const productIndex = chChanges.findIndex(change => change.path?.[0] === 'product')
  if (productIndex < 0) {
    return []
  }
  const productChange = chChanges[productIndex]
  const oldProduct = resolveRelatedHistoryItem(rel, 'Products', extractHistoryRelationId(productChange.old_value))
  const product = resolveRelatedHistoryItem(rel, 'Products', extractHistoryRelationId(productChange.value))

  return [
    buildAutofillChangeRow(ch, productIndex, 'bu', oldProduct?.bu, product?.bu),
    buildAutofillChangeRow(ch, productIndex, 'inn', oldProduct?.inn_en, product?.inn_en),
  ].filter(Boolean)
}

function buildAutofillChangeRow(ch, index, field, oldRawValue, rawValue) {
  const oldValue = displayHistoryRelation(oldRawValue)
  const value = displayHistoryRelation(rawValue)
  if (oldValue === value) {
    return null
  }

  const label = _get(fields, [ch.model_name, field]) || field
  const text = [`"${label}"`]
  if (oldValue) {
    text.push(`${historyPhrase('connectors.from')} ${historyValueHtml(oldValue, 'text-danger')}`)
  }
  if (value) {
    text.push(`${historyPhrase('connectors.to')} ${historyValueHtml(value, 'text-success')}`)
  }

  return {
    index,
    skipFields: [],
    change: {
      action: historyAction('changed'),
      text: text.join(' '),
    },
  }
}

function buildSyntheticFieldChangedRow({ label, oldValue, value, index, skipFields = [] }) {
  if (!oldValue && !value) {
    return null
  }

  const text = [`"${label}"`]
  if (oldValue) {
    text.push(`${historyPhrase('connectors.from')} ${historyValueHtml(oldValue, 'text-danger')}`)
  }
  if (value) {
    text.push(`${historyPhrase('connectors.to')} ${historyValueHtml(value, 'text-success')}`)
  }

  return {
    index,
    skipFields,
    change: {
      action: historyAction('changed'),
      text: text.join(' '),
    },
  }
}

function parseHistoryBool(value) {
  if (value === true || value === 'True' || value === 'true' || value === '1') return true
  if (value === false || value === 'False' || value === 'false' || value === '0') return false
  return null
}

function getHistoryValue(ch, change, rel, prop) {
  if (!change || !(prop in change)) {
    return ''
  }
  const prepared = { ...change, model_id: ch.model_id, model_name: ch.model_name }
  return String(getVal(ch, prepared, rel, prop) || '')
}

function historyValueHtml(value, className) {
  return DOMPurify.sanitize(`<span class="${className} d-inline-block">${value}</span>`)
}

function gmpActionDetailsLabel(ch, rel, related, className) {
  const action = rel || resolveRelatedHistoryItem(related, 'GmpForSubmissionAction', ch.model_id) || {}
  const details = [
    [historyPhrase('gmpAction.user'), displayHistoryRelation(action.user)],
    [historyPhrase('gmpAction.date'), action.date ? commons.dateLocal(action.date) : ''],
    [historyPhrase('gmpAction.text'), action.text || ''],
  ]
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${historyValueHtml(value, className)}`)

  if (details.length) {
    return details.join('; ')
  }
  return historyValueHtml(`#${ch.model_id}`, className)
}

function extractHistoryRelationId(value) {
  return extractIdFromValue(value)
}

function resolveRelatedHistoryItem(rel, modelName, id) {
  const modelData = rel?.[modelName]
  if (!modelData || id == null) {
    return null
  }
  if (Array.isArray(modelData)) {
    return modelData.find(item => String(item.id) === String(id)) || null
  }
  return modelData[id] || modelData[String(id)] || null
}

function displayHistoryRelation(value) {
  const formatted = formatRelationHistoryValue(value)
  if (formatted == null || formatted === '') {
    if (value == null) return ''
    if (typeof value !== 'object') return String(value)
    return ''
  }
  return String(formatted)
}

function formatDropdownsHistoryDetails(change) {
  const parts = []
  if (change.field) {
    parts.push(`${escapeHtmlText(historyPhrase('dropdowns.field'))}: ${escapeHtmlText(String(change.field))}`)
  }
  if (change.dd_key) {
    parts.push(`${escapeHtmlText(historyPhrase('dropdowns.key'))}: ${escapeHtmlText(String(change.dd_key))}`)
  }
  if (change.dd_value) {
    parts.push(`${escapeHtmlText(historyPhrase('dropdowns.value'))}: ${escapeHtmlText(String(change.dd_value))}`)
  }
  return parts.join('; ') || escapeHtmlText(String(change.value || change.old_value || ''))
}

function historyEntityLink(prefix, id, label) {
  const cleanId = escapeHtmlText(String(id ?? ''))
  if (!cleanId) {
    return escapeHtmlText(String(label ?? ''))
  }
  return `<a href="/${prefix}/${cleanId}">${escapeHtmlText(String(label ?? id ?? ''))}</a>`
}

function lookupHistoryRelated(rel, relName, rawId) {
  if (!rel || relName == null || rawId == null || rawId === '') {
    return null
  }
  const bucket = rel[relName]
  if (!bucket) {
    return null
  }
  const id = extractIdFromValue(rawId)
  if (typeof bucket === 'object' && !Array.isArray(bucket)) {
    return bucket[id] ?? bucket[String(id)] ?? null
  }
  if (Array.isArray(bucket)) {
    return bucket.find(item => String(item?.id) === String(id)) ?? null
  }
  return null
}

/** Подпись открытой карточки, если related истории не содержит названия продукта. */
function withCardSubject(rel, modelName, modelId) {
  const subject = String(props.subject || '').trim()
  if (
    !subject
    || props.modelName !== modelName
    || String(props.modelId) !== String(modelId)
    || modelName !== 'GmpForSubmission'
  ) {
    return rel
  }
  const record = rel && typeof rel === 'object' && !Array.isArray(rel) ? rel : {}
  if (gmpProductTitle(record, uiPrefersEnglish(locale.value))) {
    return rel
  }
  return {
    ...record,
    product_manual: false,
    product: null,
    product_manual_value: '',
    product_name: subject,
  }
}

function resolveCardHistoryEntity(ch, related) {
  // Показание, дозировка и прочие дети не подписываются именем карточки.
  if (repeatingHistoryEntity(ch, related) || isChildLifecycleRecord(ch)) {
    return null
  }
  if (
    !props.modelName ||
    !props.modelId ||
    props.modelName === 'audit_trail' ||
    props.modelName === 'Dropdowns'
  ) {
    return null
  }
  const model = models[props.modelName]
  if (!model?.entityName) {
    return null
  }
  const rel = withCardSubject(
    resolveHistoryEntityRelated(related, props.modelName, props.modelId),
    props.modelName,
    props.modelId,
  )
  const cardChange = {
    ...ch,
    model_name: props.modelName,
    model_id: props.modelId,
  }
  return model.entityName(cardChange, rel, false)
}

/** Related сущности для колонки «Объект»; для Products — с запасным поиском по id/единственной записи. */
function resolveHistoryEntityRelated(related, modelName, modelId) {
  let rel = lookupHistoryRelated(related, modelName, modelId)
  if (rel) {
    return rel
  }
  if (modelName !== 'Products') {
    return null
  }
  const bucket = related?.Products
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) {
    return null
  }
  const byId = bucket[modelId] ?? bucket[String(modelId)]
  if (byId) {
    return byId
  }
  const values = Object.values(bucket).filter(item => item && typeof item === 'object')
  if (values.length === 1) {
    return values[0]
  }
  return values.find(item => String(item.id) === String(modelId)) || null
}

function shouldSkipTaxesHistoryChange(ch, change, isAuditTrail = false) {
  const pathHead = Array.isArray(change.path) ? change.path[0] : change.path
  if (props.modelName === 'Taxes' && ch.model_name === 'Submissions' && pathHead === 'stage') {
    return true
  }
  if (ch.model_name !== 'Taxes') {
    return false
  }
  if (
    change.action === 'tax_service_note_linked'
    || change.action === 'tax_service_note_deleted'
  ) {
    return false
  }
  if (isEntityDocumentHistoryAction(change.action)) {
    return isAuditTrail
  }
  if (taxesHistorySkipFields.has(pathHead)) {
    return true
  }
  const iterableActions = ['iterable_item_added', 'iterable_item_removed', 'dictionary_item_added']
  if (iterableActions.includes(change.action)) {
    if (pathHead == null || pathHead === '' || /^\d+$/.test(String(pathHead))) {
      return true
    }
  }
  return false
}

function shouldSkipProductsHistoryChange(ch, change, isAuditTrail = false) {
  if (ch.model_name !== 'Products') {
    return false
  }
  if (!isAuditTrail) {
    return false
  }
  if (isEntityDocumentHistoryAction(change.action)) {
    return true
  }
  const pathHead = Array.isArray(change.path) ? change.path[0] : change.path
  if (productsHistorySkipFields.has(pathHead)) {
    return true
  }
  return false
}

function shouldSkipSitesHistoryChange(ch, change) {
  if (ch.model_name !== 'Sites') {
    return false
  }
  if (change.action === 'site_status_certificate_uploaded') {
    return false
  }
  if (isEntityDocumentHistoryAction(change.action)) {
    return false
  }
  const pathHead = Array.isArray(change.path) ? change.path[0] : change.path
  return sitesHistorySkipFields.has(pathHead)
}

function shouldSkipMedicinesHistoryChange(ch, change) {
  if (ch.model_name !== 'Medicines') {
    return false
  }
  const pathHead = Array.isArray(change.path) ? change.path[0] : change.path
  return medicinesHistorySkipFields.has(pathHead)
}

function buildProductRiskCategoryRows(ch, chChanges, rel) {
  if (ch.model_name !== 'Products') {
    return []
  }

  return chChanges
    .map((change, index) => buildProductRiskCategoryRow(ch, change, index, rel))
    .filter(Boolean)
}

function buildProductRiskCategoryRow(ch, change, index, rel) {
  const parsed = parseRiskCategoryChange(change)
  if (!parsed) {
    return null
  }

  const label = productRiskCategoryLabels[parsed.categoryKey] || parsed.categoryKey
  const action = change.action

  // Целый объект категории: {"level": "...", "comment": "..."}
  if (!parsed.attr) {
    if (
      !['dictionary_item_added', 'iterable_item_added', 'dictionary_item_removed', 'iterable_item_removed', 'values_changed', 'type_changes', 'exchanged'].includes(
        action
      )
    ) {
      return null
    }
    const isRemoved = ['dictionary_item_removed', 'iterable_item_removed'].includes(action)
    const oldEntry = parseRiskEntry(change.old_value)
    const newEntry = parseRiskEntry(change.value)
    if (isEmptyRiskEntry(oldEntry) && isEmptyRiskEntry(newEntry)) {
      return null
    }
    const text = isRemoved
      ? formatRiskEntryHistoryText(label, newEntry || oldEntry, rel, { removed: true })
      : oldEntry && !isEmptyRiskEntry(oldEntry) && (action === 'values_changed' || action === 'type_changes' || action === 'exchanged')
        ? formatRiskEntryChangedHistoryText(label, oldEntry, newEntry, rel)
        : formatRiskEntryHistoryText(label, newEntry || oldEntry, rel)
    if (!text) {
      return null
    }
    return {
      index,
      skipFields: ['risk_categories'],
      change: {
        action: '',
        text: DOMPurify.sanitize(text),
      },
    }
  }

  if (!['values_changed', 'type_changes', 'exchanged'].includes(action)) {
    return null
  }

  const oldVal = formatRiskCategoryAttrValue(parsed.attr, change.old_value, rel)
  const newVal = formatRiskCategoryAttrValue(parsed.attr, change.value, rel)
  if (!oldVal && !newVal) {
    return null
  }

  const text =
    parsed.attr === 'comment'
      ? formatRiskCommentHistoryText(label, oldVal, newVal)
      : formatRiskLevelHistoryText(label, oldVal, newVal)

  return {
    index,
    skipFields: ['risk_categories'],
    change: {
      action: '',
      text: DOMPurify.sanitize(text),
    },
  }
}

/**
 * Подпись вложенного поля: «Pack size ENG №1», не путь pack_blocks.0.pack_size_en.
 * @param {string} root
 * @param {number|string} index
 * @param {string} leaf
 */
function productCollectionFieldLabel(root, index, leaf) {
  const fallback = PRODUCT_COLLECTION_LEAVES[root]?.[leaf] || leaf
  const name = historyFieldLabel('Products', leaf, _get(fields, ['Products', leaf]) || fallback)
  const n = Number(index)
  if (!Number.isInteger(n) || n < 0) {
    return name
  }
  const number = n + 1
  if (root === 'pack_blocks') {
    return `${name} №${number}`
  }
  return `${name} ${number}`
}

/** «Dosage form №1: Strength №2» для добавления и удаления дозировки. */
function strengthBlockLabel(related, itemId) {
  const bucket = related?.Products
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket) || itemId == null) {
    return ''
  }
  const product = bucket[props.modelId] ?? bucket[String(props.modelId)] ?? null
  if (!product || typeof product !== 'object') return ''
  const strengthN = Number(product.strength_indexes?.[itemId] ?? product.strength_indexes?.[String(itemId)])
  const formN = Number(product.strength_form_indexes?.[itemId] ?? product.strength_form_indexes?.[String(itemId)])
  const strengthOk = Number.isInteger(strengthN) && strengthN >= 1
  const formOk = Number.isInteger(formN) && formN >= 1
  if (!strengthOk && !formOk) return ''
  const strength = strengthOk
    ? `${historyPhrase('labels.dosageItem')} №${strengthN}`
    : historyPhrase('labels.dosageItem')
  if (!formOk) return strength
  return `${historyPhrase('labels.dosageForm')} №${formN}: ${strength}`
}

/** Подпись поля карточки на стартовой: блок, этап и конкретное поле. */
function resolveHomeCardFieldLabel(change) {
  const path = normalizeHistoryPath(change?.path)
  const root = String(path[0] || '')
  if (root === 'product_journey') {
    if (path[1] === 'prototype_comment') {
      return `${historyPhrase('homeCard.journey')}: ${historyPhrase('homeCard.journeyFields.prototype_comment')}`
    }
    if (HOME_JOURNEY_STAGES.includes(String(path[1])) && HOME_JOURNEY_DATES.includes(String(path[2]))) {
      return homeJourneyLeafLabel(path[1], path[2])
    }
  }
  if (root === 'clinical_trials' && HOME_TRIAL_FIELDS.includes(String(path[2]))) {
    return homeTrialLeafLabel(path[1], path[2])
  }
  if (
    root === 'market_access'
    && HOME_MARKET_BLOCKS.includes(String(path[1]))
    && HOME_MARKET_FIELDS.includes(String(path[2]))
  ) {
    return homeMarketLeafLabel(path[1], path[2])
  }
  return null
}

function homeJourneyLeafLabel(stage, leaf) {
  return `${historyPhrase('homeCard.journey')}: ${historyPhrase(`homeCard.stages.${stage}`)} — ${historyPhrase(`homeCard.journeyFields.${leaf}`)}`
}

function homeTrialLeafLabel(index, leaf) {
  const number = Number(index)
  const n = Number.isFinite(number) ? number + 1 : index
  return `${historyPhrase('homeCard.trials')} №${n}: ${historyPhrase(`homeCard.trialFields.${leaf}`)}`
}

function homeMarketLeafLabel(block, leaf) {
  return `${historyPhrase('homeCard.market')}: ${historyPhrase(`homeCard.marketBlocks.${block}`)} — ${historyPhrase(`homeCard.marketFields.${leaf}`)}`
}

/** JSON блока карточки: обычный JSON и Python-repr с None/True/False. */
function parseHomeCardValue(value) {
  const parsed = parseHistoryJson(value)
  if (parsed && typeof parsed === 'object') return parsed
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null
  const normalized = trimmed
    .replace(/\bNone\b/g, 'null')
    .replace(/\bTrue\b/g, 'true')
    .replace(/\bFalse\b/g, 'false')
    .replace(/'/g, '"')
  try {
    return JSON.parse(normalized)
  } catch {
    return null
  }
}

function formatHomeCardText(value) {
  if (value == null || value === '' || value === 'None' || value === 'null') return ''
  if (typeof value === 'object') return ''
  return String(value).trim()
}

function formatHomeCardDate(value) {
  if (value == null || value === '' || value === 'None' || value === 'null') return ''
  const formatted = commons.dateLocal(value, 'dd.MM.yyyy')
  if (formatted && /^\d{2}\.\d{2}\.\d{4}/.test(String(formatted))) {
    return String(formatted).substring(0, 10)
  }
  return formatHomeCardText(value)
}

function formatHomeCardYesNo(value) {
  const text = formatHomeCardText(value).toLowerCase()
  if (!text) return ''
  if (['yes', 'true', '1', 'да'].includes(text)) return historyPhrase('homeCard.yes')
  if (['no', 'false', '0', 'нет'].includes(text)) return historyPhrase('homeCard.no')
  return formatHomeCardText(value)
}

/** Целый JSON блока карточки, а не одно поле внутри него. */
function shouldSkipHomeCardBlob(ch, change) {
  if (ch.model_name !== 'Products') return false
  const path = normalizeHistoryPath(change?.path)
  if (!HOME_CARD_ROOTS.includes(String(path[0] || ''))) return false
  if (resolveHomeCardFieldLabel(change)) return false
  return [
    'iterable_item_added',
    'iterable_item_removed',
    'dictionary_item_added',
    'dictionary_item_removed',
    'type_changes',
    'values_changed',
  ].includes(change.action)
}

function pushHomeJourneyLeaves(leaves, stage, source) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return
  for (const leaf of HOME_JOURNEY_DATES) {
    const text = formatHomeCardDate(source[leaf])
    if (!text) continue
    leaves.push({ key: `${stage}.${leaf}`, label: homeJourneyLeafLabel(stage, leaf), text })
  }
}

function homeCardLeaves(raw, path) {
  const parsed = parseHomeCardValue(raw)
  const root = String(path[0] || '')
  const leaves = []
  if (!parsed || typeof parsed !== 'object') return leaves
  if (root === 'product_journey') {
    if (path.length >= 2 && HOME_JOURNEY_STAGES.includes(String(path[1]))) {
      pushHomeJourneyLeaves(leaves, path[1], parsed)
    } else if (path.length <= 1 && !Array.isArray(parsed)) {
      for (const stage of HOME_JOURNEY_STAGES) {
        pushHomeJourneyLeaves(leaves, stage, parsed[stage])
      }
      const comment = formatHomeCardText(parsed.prototype_comment)
      if (comment) {
        leaves.push({
          key: 'prototype_comment',
          label: `${historyPhrase('homeCard.journey')}: ${historyPhrase('homeCard.journeyFields.prototype_comment')}`,
          text: comment,
        })
      }
    }
  }
  if (root === 'clinical_trials') {
    const items = path.length >= 2 && !Array.isArray(parsed)
      ? [{ index: path[1], fields: parsed }]
      : Array.isArray(parsed)
        ? parsed.map((fields, index) => ({ index, fields: fields || {} }))
        : []
    for (const item of items) {
      if (!item.fields || typeof item.fields !== 'object') continue
      for (const leaf of HOME_TRIAL_FIELDS) {
        const text = leaf === 'code'
          ? formatHomeCardText(item.fields[leaf])
          : formatHomeCardDate(item.fields[leaf])
        if (!text) continue
        leaves.push({
          key: `${item.index}.${leaf}`,
          label: homeTrialLeafLabel(item.index, leaf),
          text,
        })
      }
    }
  }
  if (root === 'market_access' && !Array.isArray(parsed)) {
    const blocks = path.length >= 2 && HOME_MARKET_BLOCKS.includes(String(path[1]))
      ? [[String(path[1]), parsed]]
      : HOME_MARKET_BLOCKS.filter(block => parsed[block]).map(block => [block, parsed[block]])
    for (const [block, fields] of blocks) {
      if (!fields || typeof fields !== 'object') continue
      for (const leaf of HOME_MARKET_FIELDS) {
        const text = leaf === 'value'
          ? formatHomeCardYesNo(fields[leaf])
          : formatHomeCardDate(fields[leaf])
        if (!text) continue
        leaves.push({
          key: `${block}.${leaf}`,
          label: homeMarketLeafLabel(block, leaf),
          text,
        })
      }
    }
  }
  return leaves
}

function homeCardLeafAlreadyPresent(chChanges, root, key) {
  return (chChanges || []).some(change => {
    if (shouldSkipHomeCardBlob({ model_name: 'Products' }, change)) return false
    const path = normalizeHistoryPath(change?.path)
    if (String(path[0]) !== root || !resolveHomeCardFieldLabel(change)) return false
    if (root === 'product_journey' && key === 'prototype_comment') {
      return path[1] === 'prototype_comment'
    }
    if (root === 'product_journey') {
      const [stage, leaf] = key.split('.')
      return path[1] === stage && path[2] === leaf
    }
    if (root === 'clinical_trials') {
      const [index, leaf] = key.split('.')
      return String(path[1]) === String(index) && path[2] === leaf
    }
    if (root === 'market_access') {
      const [block, leaf] = key.split('.')
      return path[1] === block && path[2] === leaf
    }
    return false
  })
}

function buildHomeCardHistoryRows(ch, chChanges) {
  if (ch.model_name !== 'Products') return []
  const rows = []
  chChanges.forEach((change, index) => {
    if (!shouldSkipHomeCardBlob(ch, change)) return
    const path = normalizeHistoryPath(change.path)
    const root = String(path[0])
    const removed = String(change.action || '').includes('removed')
    const newLeaves = removed ? [] : homeCardLeaves(change.value, path)
    const oldLeaves = homeCardLeaves(
      removed ? (change.old_value ?? change.value) : change.old_value,
      path,
    )
    const keys = new Set([
      ...oldLeaves.map(leaf => leaf.key),
      ...newLeaves.map(leaf => leaf.key),
    ])
    for (const key of keys) {
      if (homeCardLeafAlreadyPresent(chChanges, root, key)) continue
      const previous = oldLeaves.find(leaf => leaf.key === key)
      const next = newLeaves.find(leaf => leaf.key === key)
      const oldValue = previous?.text || ''
      const value = next?.text || ''
      if (oldValue === value) continue
      const row = buildSyntheticFieldChangedRow({
        label: (next || previous).label,
        oldValue,
        value,
        index,
      })
      if (row) rows.push(row)
    }
  })
  return rows
}

function resolveProductCollectionFieldLabel(change) {
  const path = normalizeHistoryPath(change?.path)
  const leaf = String(path[path.length - 1] || '')
  // Старая запись shelf-life / storage без индекса блока — это первый блок.
  if (path.length === 1 && PRODUCT_COLLECTION_LEAVES.pack_blocks[leaf]) {
    return productCollectionFieldLabel('pack_blocks', 0, leaf)
  }
  const root = String(path[0] || '')
  if (!PRODUCT_COLLECTION_LEAVES[root] || path.length < 3) {
    return null
  }
  if (!PRODUCT_COLLECTION_LEAVES[root][leaf]) {
    return null
  }
  return productCollectionFieldLabel(root, path[1], leaf)
}

/** Первое появление Shelf-life / Storage conditions — это добавление ключа, не правка. */
function normalizePackScalarHistoryChange(change) {
  const path = normalizeHistoryPath(change?.path)
  const leaf = String(path[path.length - 1] || '')
  const scalar = path.length === 1 && (leaf === 'shelf_life_ru' || leaf === 'storage_conditions_ru')
  const nested = path[0] === 'pack_blocks' && path.length >= 3 && PRODUCT_COLLECTION_LEAVES.pack_blocks[leaf]
  if (!scalar && !nested) {
    return
  }
  if (!['dictionary_item_added', 'type_changes'].includes(change.action)) {
    return
  }
  change.action = 'values_changed'
  if (
    change.old_value == null
    || change.old_value === 'unknown___'
    || change.old_value === 'None'
    || change.old_value === 'null'
  ) {
    change.old_value = ''
  }
}

/** Чипы Pack size в истории через запятую, не через перевод строки. */
function formatPackChipHistoryText(value) {
  return String(value ?? '')
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .join(', ')
}

function formatPackLeafHistoryText(leaf, value) {
  const text = String(value ?? '').trim()
  if (leaf === 'pack_size_en' || leaf === 'pack_size_ru') {
    return formatPackChipHistoryText(text)
  }
  return text
}

/**
 * Плоская колонка pack_size / shelf-life / storage дублирует поле блока.
 * Старые записи содержат обе; показываем только строку блока.
 */
function shouldSkipMirroredPackColumn(ch, change, chChanges) {
  if (ch.model_name !== 'Products') {
    return false
  }
  const path = normalizeHistoryPath(change?.path)
  if (path.length !== 1 || !PRODUCT_COLLECTION_LEAVES.pack_blocks[path[0]]) {
    return false
  }
  const leaf = String(path[0])
  return (chChanges || []).some(other => {
    const otherPath = normalizeHistoryPath(other?.path)
    if (otherPath[0] !== 'pack_blocks') {
      return false
    }
    if (otherPath.length >= 3) {
      return String(otherPath[otherPath.length - 1]) === leaf
    }
    return otherPath.length > 0
  })
}

/** Служебные строки pack_blocks/po_entries: целиком JSON блока и внутренний id. */
function shouldSkipProductCollectionBlob(ch, change) {
  if (ch.model_name !== 'Products') {
    return false
  }
  const path = normalizeHistoryPath(change?.path)
  const root = String(path[0] || '')
  if (!PRODUCT_COLLECTION_LEAVES[root]) {
    return false
  }
  if (path.length >= 3 && String(path[path.length - 1]) === 'id') {
    return true
  }
  if (path.length > 2) {
    return false
  }
  // Первое сохранение: весь pack_blocks меняет тип None → список, без полей.
  if (path.length === 1 && change.action === 'type_changes') {
    return true
  }
  return [
    'iterable_item_added',
    'iterable_item_removed',
    'dictionary_item_added',
    'dictionary_item_removed',
  ].includes(change.action)
}

function buildProductCollectionRows(ch, chChanges) {
  if (ch.model_name !== 'Products') {
    return []
  }
  const leafKeys = new Set()
  for (const change of chChanges) {
    const path = normalizeHistoryPath(change.path)
    if (path.length >= 3 && PRODUCT_COLLECTION_LEAVES[path[0]]) {
      leafKeys.add(`${path[0]}.${path[1]}.${path[2]}`)
    }
  }
  const rows = []
  chChanges.forEach((change, index) => {
    if (!shouldSkipProductCollectionBlob(ch, change)) {
      return
    }
    const path = normalizeHistoryPath(change.path)
    const root = String(path[0])
    const removed = String(change.action || '').includes('removed')
    const newItems = removed ? [] : productCollectionItems(change.value, path)
    const oldItems = productCollectionItems(
      removed ? (change.old_value ?? change.value) : change.old_value,
      path,
    )
    const byIndex = new Map()
    for (const item of oldItems) {
      byIndex.set(String(item.index), { oldFields: item.fields, newFields: {} })
    }
    for (const item of newItems) {
      const key = String(item.index)
      const slot = byIndex.get(key) || { oldFields: {}, newFields: {} }
      slot.newFields = item.fields
      byIndex.set(key, slot)
    }
    for (const [itemIndex, slot] of byIndex) {
      for (const leaf of Object.keys(PRODUCT_COLLECTION_LEAVES[root] || {})) {
        if (leafKeys.has(`${root}.${itemIndex}.${leaf}`)) {
          continue
        }
        const oldText = formatPackLeafHistoryText(leaf, slot.oldFields?.[leaf])
        const newText = formatPackLeafHistoryText(leaf, slot.newFields?.[leaf])
        if (oldText === newText) {
          continue
        }
        const row = buildSyntheticFieldChangedRow({
          label: productCollectionFieldLabel(root, itemIndex, leaf),
          oldValue: oldText,
          value: newText,
          index,
        })
        if (row) {
          rows.push(row)
        }
      }
    }
  })
  return rows
}

function productCollectionItems(raw, path) {
  const parsed = parseHistoryJson(raw)
  if (path.length >= 2 && parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return [{ index: path[1], fields: parsed }]
  }
  if (Array.isArray(parsed)) {
    return parsed
      .map((item, index) => (
        item && typeof item === 'object' && !Array.isArray(item)
          ? { index, fields: item }
          : null
      ))
      .filter(Boolean)
  }
  return []
}

function parseHistoryJson(value) {
  if (value == null || value === '') {
    return null
  }
  if (typeof value !== 'string') {
    return value
  }
  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) {
    return null
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    try {
      return JSON.parse(trimmed.replace(/'/g, '"'))
    } catch {
      return null
    }
  }
}

function shouldSkipProductRiskCategoryChange(ch, change) {
  if (ch.model_name !== 'Products') {
    return false
  }
  if (isEmptyRiskCategoryIterableChange(change)) {
    return true
  }
  return Boolean(parseRiskCategoryChange(change))
}

function parseRiskCategoryChange(change) {
  const path = change?.path
  if (!path) {
    return null
  }

  const segments = Array.isArray(path) ? path.map(String) : [String(path)]
  if (segments[0] === 'risk_categories' && segments.length >= 3) {
    return { categoryKey: segments[1], attr: segments[2] }
  }
  if (segments[0] === 'risk_categories' && segments.length === 2) {
    return { categoryKey: segments[1], attr: null }
  }

  const flat = segments.join('.')
  const attrMatch = flat.match(/^risk_categories\.([^.]+)\.(level|comment)$/)
  if (attrMatch) {
    return { categoryKey: attrMatch[1], attr: attrMatch[2] }
  }
  const entryMatch = flat.match(/^risk_categories\.([^.]+)$/)
  if (entryMatch) {
    return { categoryKey: entryMatch[1], attr: null }
  }

  return null
}

function isEmptyRiskCategoryIterableChange(change) {
  if (!['dictionary_item_added', 'iterable_item_added', 'iterable_item_removed', 'dictionary_item_removed'].includes(change.action)) {
    return false
  }
  const path = change?.path
  const segments = Array.isArray(path) ? path.map(String) : [String(path || '')]
  const isRiskPath =
    segments[0] === 'risk_categories' ||
    segments.some(segment => String(segment).startsWith('risk_categories'))
  if (!isRiskPath) {
    return false
  }
  return isEmptyRiskEntry(change.value) && isEmptyRiskEntry(change.old_value)
}

function parseRiskEntry(value) {
  if (value == null || value === '') {
    return null
  }
  let entry = value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    try {
      entry = JSON.parse(trimmed)
    } catch {
      try {
        entry = JSON.parse(trimmed.replace(/'/g, '"'))
      } catch {
        return null
      }
    }
  }
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return null
  }
  return entry
}

function isEmptyRiskEntry(value) {
  const entry = parseRiskEntry(value)
  if (!entry) {
    return value == null || value === ''
  }
  return !String(entry.level || '').trim() && !String(entry.comment || '').trim()
}

function formatRiskCategoryAttrValue(attr, raw, rel) {
  if (raw == null || raw === '') {
    return ''
  }
  if (attr === 'level') {
    return formatRiskLevelLabel(raw, rel)
  }
  return String(raw).trim()
}

function formatRiskLevelLabel(raw, rel) {
  const key = String(raw).trim().toLowerCase()
  if (!key) {
    return ''
  }
  const configStore = useConfigsStore()
  const options = configStore.get('products.risk_level', [])?.value || []
  const fromConfig = options.find(opt => String(opt?.key || '').toLowerCase() === key || String(opt?.id) === key)
  if (fromConfig?.value) {
    return fromConfig.value
  }
  const levelLabels = {
    low: historyPhrase('risk.levels.low'),
    medium: historyPhrase('risk.levels.medium'),
    high: historyPhrase('risk.levels.high'),
  }
  if (levelLabels[key]) {
    return levelLabels[key]
  }
  const fromRelated = resolveRelatedHistoryItem(rel, 'Dropdowns', raw)
  if (fromRelated) {
    return displayHistoryRelation(fromRelated)
  }
  if (rel?.Dropdowns && typeof rel.Dropdowns === 'object' && !Array.isArray(rel.Dropdowns)) {
    return rel.Dropdowns[raw] || rel.Dropdowns[String(raw)] || key
  }
  return key
}

function formatRiskEntryHistoryText(label, entry, rel, { removed = false } = {}) {
  const parsed = parseRiskEntry(entry)
  if (!parsed) {
    return ''
  }
  const levelLabel = historyPhrase('risk.level')
  const commentLabel = historyPhrase('risk.comment')
  const level = formatRiskLevelLabel(parsed.level, rel)
  const comment = String(parsed.comment || '').trim()
  const parts = []
  if (level) {
    parts.push(`${levelLabel}: ${historyValueHtml(level, removed ? 'text-danger' : 'text-success')}`)
  }
  if (comment) {
    parts.push(`${commentLabel}: ${historyValueHtml(comment, removed ? 'text-danger' : 'text-success')}`)
  }
  if (!parts.length) {
    return ''
  }
  const prefix = removed ? historyPhrase('risk.removed', { label }) : label
  return `${prefix}: ${parts.join('; ')}`
}

function formatRiskEntryChangedHistoryText(label, oldEntry, newEntry, rel) {
  const oldParsed = parseRiskEntry(oldEntry) || {}
  const newParsed = parseRiskEntry(newEntry) || {}
  const parts = []
  const oldLevel = formatRiskLevelLabel(oldParsed.level, rel)
  const newLevel = formatRiskLevelLabel(newParsed.level, rel)
  if (oldLevel !== newLevel) {
    parts.push(formatRiskLevelHistoryText(label, oldLevel, newLevel))
  }
  const oldComment = String(oldParsed.comment || '').trim()
  const newComment = String(newParsed.comment || '').trim()
  if (oldComment !== newComment) {
    parts.push(formatRiskCommentHistoryText(label, oldComment, newComment))
  }
  return parts.filter(Boolean).join('; ')
}

function formatRiskLevelHistoryText(label, oldVal, newVal) {
  const levelLabel = historyPhrase('risk.level')
  const next = historyValueHtml(newVal || '—', 'text-success')
  if (oldVal) {
    const prev = historyValueHtml(oldVal, 'text-danger')
    return `${label}: ${levelLabel} ${historyPhrase('connectors.from')} ${prev} ${historyPhrase('connectors.to')} ${next}`
  }
  return `${label}: ${levelLabel} ${historyPhrase('connectors.to')} ${next}`
}

function formatRiskCommentHistoryText(label, oldVal, newVal) {
  const commentLabel = historyPhrase('risk.comment')
  const next = historyValueHtml(newVal || '—', 'text-success')
  if (oldVal) {
    const prev = historyValueHtml(oldVal, 'text-danger')
    return `${label}: ${commentLabel} ${historyPhrase('connectors.from')} ${prev} ${historyPhrase('connectors.to')} ${next}`
  }
  return `${label}: ${commentLabel} ${historyPhrase('connectors.to')} ${next}`
}

function buildUploadForViewer(ch, change, rel) {
  const filename = change.filename || ch.changes?.[0]?.filename || rel?.filename || null
  const secureFilename =
    change.secure_filename || ch.changes?.[0]?.secure_filename || rel?.secure_filename || null
  if (!filename || !secureFilename) return null
  return {
    filename: String(filename),
    secure_filename: String(secureFilename),
    author: rel?.author || { email: '', fio: '', username: '' },
    uploaded_at: rel?.uploaded_at || null,
    version: rel?.version || '',
  }
}

function entityText(step) {
  if (!step?.entity) return ''
  if (!step.entityUpload?.filename) return step.entity
  const fn = step.entityUpload.filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return step.entity.replace(new RegExp(`\\s*${fn}\\s*$`), '').replace(/\s+/g, ' ').trim()
}

function searchFieldset(data, fieldName) {
  if (!data.RequestCategory) {
    return null
  }
  const request_data = data.RequestCategory

  const mergeFieldsets = request_data => {
    return request_data.reduce((acc, category) => {
      return { ...acc, ...category.fieldset }
    }, {})
  }

  const findFieldValue = fieldName => {
    const mergedFieldset = mergeFieldsets(request_data)
    return mergedFieldset[fieldName] || null
  }

  return findFieldValue(fieldName)
}

function extractIdFromValue(val) {
  // Обработка словаря с id - сначала пытаемся распарсить строку
  if (typeof val === 'string') {
    try {
      // Пробуем стандартный JSON.parse
      const parsed = JSON.parse(val)
      if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
        return parsed.id
      }
    } catch {
      // Если не удалось, пробуем заменить одинарные кавычки на двойные (Python dict формат)
      try {
        const normalized = val.replace(/'/g, '"')
        const parsed = JSON.parse(normalized)
        if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
          return parsed.id
        }
      } catch {
        // Если и это не сработало, пытаемся извлечь id через регулярное выражение
        const idMatch = val.match(/'id'\s*:\s*(\d+)/)
        if (idMatch) {
          return parseInt(idMatch[1], 10)
        }
      }
    }
  } else if (val && typeof val === 'object' && val.id !== undefined) {
    return val.id
  }
  return val
}

function getVal(ch, change, rel, prop = 'value') {
  const v = values()
  const fullPath = normalizeHistoryPath(change.path)
  const formatterPath = historyFieldPath(change)

  // Несколько РУ у ЛП — как в QDMS
  if (fullPath[0] === 'registration_certificates') {
    const isDateField = fullPath.includes('date') || formatterPath[formatterPath.length - 1] === 'date'
    const fmt = isDateField
      ? _get(v, [ch.model_name, 'registration_at']) || _get(v, ['registration_at'])
      : _get(v, [ch.model_name, 'registration_certificates']) || _get(v, ['registration_certificates'])
    if (typeof fmt === 'function') {
      let val = fmt(ch, change, rel, prop)
      if (isRef(val) || isReactive(val)) val = toValue(val)
      return val ?? ''
    }
  }

  let res =
    (formatterPath.length
      ? _get(v, [ch.model_name, ...formatterPath]) || _get(v, formatterPath)
      : null) ||
    (formatterPath.length >= 2
      ? _get(v, [ch.model_name, formatterPath.join('.')])
      : null) ||
    _get(v, [ch.model_name, ...normalizeHistoryPath(change.path)]) ||
    _get(v, normalizeHistoryPath(change.path))
  if (res && typeof res === 'object' && !Array.isArray(res)) {
    const leaf = formatterPath.length ? formatterPath[formatterPath.length - 1] : null
    if (leaf && typeof res[leaf] === 'function') {
      res = res[leaf]
    } else if (typeof res.default === 'function') {
      res = res.default
    }
  }
  const hasCustomFormatter = typeof res === 'function'
  if (!hasCustomFormatter) {
    const pathLeaf = formatterPath.length ? String(formatterPath[formatterPath.length - 1]) : ''
    const dateOnlyFields = new Set([
      'doc_date',
      'registration_at',
      'ru_reissue_date',
      'date',
    ])
    const forceDateOnly = dateOnlyFields.has(pathLeaf)
    res = (ch, change, rel, prop) => {
      const raw = change[prop]
      if (isFacsimileBinaryHistoryValue(raw)) {
        return t('signatureScan')
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (/^\d{2}\.\d{2}\.\d{4}/.test(trimmed) && (forceDateOnly || trimmed.length === 10)) {
          return trimmed.substring(0, 10)
        }
        const isDateOnly = trimmed.length === 10 && trimmed[4] === '-' && trimmed[7] === '-'
        if (isDateOnly) {
          return commons.dateLocal(trimmed, 'dd.MM.yyyy') || trimmed
        }
        const isIsoDate = trimmed.length >= 19 && trimmed[4] === '-' && trimmed.includes(':')
        if (isIsoDate) {
          const d = new Date(trimmed)
          if (!isNaN(d.getTime())) {
            if (
              forceDateOnly ||
              (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)
            ) {
              return commons.dateLocal(trimmed, 'dd.MM.yyyy') || trimmed
            }
            return commons.dateTimeLocal(trimmed) || trimmed
          }
        }
      }
      return raw
    }
  }

  if ('value' in change) {
    change.value = extractIdFromValue(change.value)
  }
  if ('old_value' in change) {
    change.old_value = extractIdFromValue(change.old_value)
  }

  let val = res(ch, change, rel, prop)
  if (isRef(val) || isReactive(val)) {
    val = toValue(val)
  }

  const pathParts = normalizeHistoryPath(change.path)
  const class_c =
    change.relation || findClass(rel?.map || [], ch.model_name, historyRelationField(change))
  const rawText = change[prop] == null || change[prop] === '' ? '' : String(change[prop])
  const shownText = val == null || val === '' ? '' : String(val)
  // Форматтер мог вернуть id числом: «2» и 2 — одно и то же сырое значение.
  if (class_c && rawText && (!shownText || shownText === rawText)) {
    const resolved = lookupHistoryRelated(rel, class_c, change[prop])
    if (resolved) {
      const text = displayHistoryRelation(resolved)
      if (text && text !== rawText) {
        val = text
      }
    }
  }
  if (rawText && String(val ?? '') === rawText && !isNaN(Number(val)) && Number(val) !== 0) {
    const numericVal = Number(val)
    if (
      class_c &&
      Object.prototype.hasOwnProperty.call(rel, class_c)
    ) {
      const class_entries = rel[class_c]
      if (Array.isArray(class_entries)) {
        if (class_c === 'Dropdowns') {
          const field_obj = class_entries.find(d => d.id === numericVal)

          if (field_obj) {
            val = field_obj.value
          }
        } else {
          const field_obj = class_entries.find(d => d.id === numericVal)
          if (field_obj) {
            if (class_c === 'Role' || class_c === 'NotificationSettings') {
              val = field_obj?.caption || field_obj?.name
            } else {
              val = field_obj?.name
              if (field_obj?.full_name) val = field_obj.full_name
            }
          }
        }
      } else if (class_entries && typeof class_entries === 'object') {
        const field_obj = class_entries[numericVal] ?? class_entries[String(numericVal)]
        if (typeof field_obj === 'string' || typeof field_obj === 'number') {
          val = String(field_obj)
        } else if (field_obj) {
          if (class_c === 'Role' || class_c === 'NotificationSettings') {
            val = field_obj?.caption || field_obj?.name || val
          } else {
            const text = displayHistoryRelation(field_obj)
            if (text) val = text
          }
        }
      }
    }
  }
  if (
    typeof val === 'string'
    && (pathParts[pathParts.length - 1] === 'pack_size_en' || pathParts[pathParts.length - 1] === 'pack_size_ru')
  ) {
    val = formatPackChipHistoryText(val)
  }
  return val ?? ''
}

function findClass(relMap, modelName, fieldName) {
  if (!Array.isArray(relMap)) return null

  const modelEntry = relMap.find(entry => entry.model === modelName)
  if (!modelEntry) return null

  if (modelEntry.field === fieldName) return modelEntry.class
  if (!Array.isArray(modelEntry.fields)) return null

  const fieldEntry = modelEntry.fields.find(field => field.field === fieldName)
  return fieldEntry ? fieldEntry.class : null
}

/** Текст действия без обёртки span. */
function plainHistoryAction(html) {
  return String(html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

/**
 * Второе «добавлено» той же карточки PL (повтор Created или чужая запись
 * с тем же названием в колонке «Объект») не показываем.
 */
function collapseDuplicateProductAdds(steps) {
  if (props.modelName !== 'Products') {
    return steps
  }
  const bareAdded = plainHistoryAction(historyAction('added'))
  const productEntities = new Set()
  for (const step of steps) {
    const keys = step.rowKeys || []
    if (!keys.some(key => String(key).startsWith('Products:'))) continue
    if (!isBareAddedStep(step, bareAdded)) continue
    productEntities.add(step.entity || '')
  }
  if (!productEntities.size) return steps
  const seen = new Set()
  const collapsed = []
  for (const step of steps) {
    const entity = step.entity || ''
    if (productEntities.has(entity) && isBareAddedStep(step, bareAdded)) {
      if (seen.has(entity)) continue
      seen.add(entity)
      if (step.changes.length > 1) {
        step.changes = [step.changes[0]]
      }
    }
    collapsed.push(step)
  }
  return collapsed
}

/**
 * Для подачи с записью «создано на основании» пустое «добавлено» не показываем —
 * основание и есть первая запись о создании.
 */
function hideBareSubmissionAddWhenCreatedFromProduct(steps) {
  if (props.modelName !== 'Submissions') {
    return steps
  }
  const bareAdded = plainHistoryAction(historyAction('added'))
  const createdOnBasis = plainHistoryAction(historyAction('createdOnBasis'))
  const hasBasis = steps.some(step =>
    (step.changes || []).some(
      change => plainHistoryAction(change?.action) === createdOnBasis
    )
  )
  if (!hasBasis) {
    return steps
  }
  return steps.filter(step => !isBareAddedStep(step, bareAdded))
}

/** Строка истории — только «добавлено» без текста в деталях. */
function isBareAddedStep(step, bareAdded) {
  const changes = step?.changes || []
  if (!changes.length) return false
  return changes.every(change => {
    if (change?.upload) return false
    const text = plainHistoryAction(change?.text)
    return plainHistoryAction(change?.action) === bareAdded && !text
  })
}

function _pick(raw) {
  const hist = []
  if (!raw || typeof raw !== 'object') {
    return hist
  }
  // на случай, если список передали без нормализации модалки
  let inner = raw
  if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    inner = raw.data
  }
  const history = Array.isArray(inner.history) ? inner.history : []
  const related =
    inner.related && typeof inner.related === 'object' && !Array.isArray(inner.related)
      ? inner.related
      : {}

  const h = props.limit ? history.slice(0, props.limit) : history.slice()
  for (const batch of h) {
    const batchRows = Array.isArray(batch)
      ? batch
      : batch && typeof batch === 'object'
        ? [batch]
        : []
    if (!batchRows.length) continue
    const first = batchRows[0]
    if (!first || typeof first !== 'object') continue

    const base = {
      date: commons.dateLocal(first.date, 'dd.MM.yyyy, HH:mm'),
      author: historyKnownLabel(first.fio || first.username),
    }

    // Одна битая запись (неизвестный model_name и т.п.) не должна ронять всю модалку.
    const items = batchRows
      .map(row => {
        try {
          return formatDiff.call(related, row)
        } catch (err) {
          console.warn('[history] formatDiff failed', row?.model_name, row?.model_id, err)
          return {
            entity: safeHistoryEntity(related, row),
            key: `${row?.model_name || '?'}:${row?.model_id ?? '?'}`,
            changes: fallbackFieldChanges(row, related),
          }
        }
      })
      .map(item => ({
        ...item,
        changes: Array.isArray(item.changes)
          ? item.changes.filter(
              ch =>
                (ch.action?.trim() || ch.text?.trim() || ch.upload)
                && !isFacsimileBinaryHistoryText(ch.text)
            )
          : [],
      }))
      .filter(item => item.changes.length > 0)

    if (!items.length) continue

    const byEntity = new Map()
    for (const item of items) {
      const { entity, changes, entityUpload, key: histRowKey } = item
      const rowChanges = Array.isArray(changes) ? changes : []
      // История дропдаунов, Audit Trail и показаний: не сшивать по тексту «Объект»,
      // а по model_name:id — иначе несколько показаний с похожим текстом сливаются в одну строку.
      const separateById =
        (props.modelName === 'Dropdowns' || props.modelName === 'audit_trail') &&
        typeof histRowKey === 'string' &&
        histRowKey.startsWith('Dropdowns:')
      const indicationRow =
        typeof histRowKey === 'string' && histRowKey.startsWith('ProductIndications:')
      const mapKey = separateById || indicationRow ? histRowKey : entity ?? '__none__'
      if (!byEntity.has(mapKey)) {
        byEntity.set(mapKey, {
          ...base,
          entity,
          mergeKey: separateById || indicationRow ? histRowKey : undefined,
          rowKeys: [],
          entityUpload: null,
          changes: [],
        })
      }
      const grouped = byEntity.get(mapKey)
      grouped.changes.push(...rowChanges)
      if (histRowKey) grouped.rowKeys.push(histRowKey)
      if (separateById || indicationRow) grouped.entity = entity
      if (!grouped.entityUpload && entityUpload) grouped.entityUpload = entityUpload
    }

    hist.push(...byEntity.values())
  }

  // audit_trail и история выпадающих списков: одна операция — несколько полей → одна строка в таблице
  if (props.modelName === 'audit_trail' || props.modelName === 'Dropdowns') {
    const dedupeKey = row => {
      if (row.mergeKey) {
        return `${row.date}|${row.author}|${row.mergeKey}`
      }
      return `${row.date}|${row.author}|${(row.entity ?? '').trim()}`
    }
    const merged = []
    for (const row of hist) {
      const k = dedupeKey(row)
      const existing = merged.find(m => dedupeKey(m) === k)
      const rowCh = Array.isArray(row.changes) ? row.changes : []
      if (existing) {
        existing.changes.push(...rowCh)
        if (row.mergeKey && row.entity) existing.entity = row.entity
      } else {
        merged.push({ ...row, changes: [...rowCh] })
      }
    }
    return merged
  }

  return hideBareSubmissionAddWhenCreatedFromProduct(
    collapseDuplicateProductAdds(hist)
  )
}
</script>

<i18n>
{
  "ru": {
    "filter": {
      "dateFrom": "Дата с",
      "dateTo": "Дата по",
      "reset": "Сбросить"
    },
    "col": {
      "date": "Дата",
      "object": "Объект",
      "action": "Действие",
      "details": "Детали"
    },
    "empty": "История изменений отсутствует",
    "registrationCertificates": "Регистрационные удостоверения",
    "registrationCertificateNumber": "№ Регистрационного удостоверения",
    "registrationCertificateReference": "{label} (РУ «{number}»)",
    "registrationCertificatePosition": "{label} (РУ #{position})",
    "serviceNoteScan": "Скан служебной записки",
    "signatureScan": "скан подписи"
  },
  "en": {
    "filter": {
      "dateFrom": "Date from",
      "dateTo": "Date to",
      "reset": "Reset"
    },
    "col": {
      "date": "Date",
      "object": "Object",
      "action": "Action",
      "details": "Details"
    },
    "empty": "No change history",
    "registrationCertificates": "Registration certificates",
    "registrationCertificateNumber": "Registration certificate No.",
    "registrationCertificateReference": "{label} (RC “{number}”)",
    "registrationCertificatePosition": "{label} (RC #{position})",
    "serviceNoteScan": "Service note scan",
    "signatureScan": "signature scan"
  }
}
</i18n>
